import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { taskPath } from '@/constants/routes';
import { Modal } from '@/components/ui/Modal';
import { DrawerDock } from '@/components/ui/DrawerDock';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Chevron, ChevronDown, Detach, Eye, ExternalLink, Grip, MoreVertical, OpenPanel, Plus, PriorityChevron, SubItem, TaskTypeGlyph, Trash } from '@/components/ui/icons';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import StatusDot, { closedLabel, isClosedKind, statusKind } from '@/components/status-dot';
import MarkdownEditor from '@/components/markdown-editor';
import { useMemberCreate } from '@/components/new-member';
import MentionInput from '@/components/mention-input';
import CommentRow from '@/components/comment-row';
import { useLightbox } from '@/components/lightbox';
import { attachmentKind, isPreviewable } from '@/features/backlog/attachments';
import { useRealtime } from '@/features/realtime/realtime';
import { CLIENT_ID } from '@/utils/http';
import { useWatches } from '@/features/backlog/watches';
import { useIdentity } from '@/features/identity/identity';
import { Popover, menuRow } from '@/components/ui/Popover';
import { useItemPanel } from '@/components/item-panel';
import { Field, inputCls, Label } from '@/components/ui/form';
import { DateRow, EpicRow, LabelsRow, MemberRow, ParentRow, PriorityRow, PropertySummary, ReporterRow, SourceRow, StatusRow, TypeRow } from '@/components/item-property-fields';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';
import { useAppData } from '@/app-data';
import * as api from '@/features/backlog/api';
import ItemLinksSection from '@/components/item-links-section';
import ItemWikiPagesSection from '@/components/item-wiki-pages-section';
import { Activity, Attachment, Comment, Item, ItemLink, Member, Priority, Status, TaskType } from '@/features/backlog/types';
import { DEFAULT_PRIORITY, DEFAULT_TASK_TYPE, PRIORITIES, priorityMeta, taskTypeMeta, SOURCES } from '@/features/backlog/constants';
import { ITEM_PARAM } from '@/features/backlog/url-state';
import { SelectOption, SelectPopover } from '@/components/ui/SelectPopover';
import { StatusMeta, useStatuses, useStatusMeta } from '@/features/backlog/statuses';
import { buildStatusTimeline, formatDays } from '@/features/backlog/status-timeline';
import { useHistory } from '@/features/history/history';
import { existenceCommand, removalCommand, snapshotCommand } from '@/features/history/commands';
import { commentTime, fmtDate, fmtDateTime, timeAgo } from '@/features/backlog/format';
import MemberAvatar from '@/components/member-avatar';

type Props = {
  open: boolean;
  onClose: () => void;
  itemId: string | null; // null => create mode
  boardId: string;
  onSaved: () => void;
  /** Default status when creating (e.g. the kanban column a "+" was clicked in). */
  initialStatus?: string;
  /** Default parent when creating a sub-item. */
  initialParentId?: string | null;
  /** Default task type when creating (e.g. 'epic' from the epic sidebar's New button). */
  initialType?: string;
  /** Epic to adopt when creating (the epic the rail is focused on — so a new item made from a
   *  filtered epic view is linked to that epic). Ignored for epic-typed items and sub-items. */
  initialEpicId?: string | null;
  /** How to present the details. 'auto' (default) = modal or docked side panel (the quick-edit
   *  drawer). 'page' = an inline, full-width block with no overlay/dock chrome — used by the
   *  full-screen task route (/:slug/:key), reusing the same body verbatim. */
  presentation?: 'auto' | 'page';
  /** Tab to open on (e.g. 'comments' when a row's comments button was clicked). Ignored in
   *  create mode, where only Details exists — the comments/activity tabs need a saved item. */
  initialTab?: ItemTab;
  /** All items on the board — used to populate the Parent picker. */
  items?: Item[];
  /** Deep-link/highlight target from a notification click: `comment:<id>` | `field:<name>` |
   *  `properties` | `attachments`. The drawer opens the matching tab, scrolls the element
   *  (`[data-hl]`) into view and runs the flash animation. */
  highlight?: string;
  /** Deep-link `?att=<id>`: once this item's attachments load, open the lightbox on that attachment
   *  (so a copied attachment link opens straight to it). */
  openAttachment?: string;
};

/** The drawer's tab keys — exported so a view can deep-link straight to one (e.g. a row's
 *  comments button opens the drawer on 'comments'). */
export type ItemTab = 'details' | 'comments' | 'activity' | 'stats';
type Tab = ItemTab;

type Form = {
  title: string;
  description: string;
  status: string;
  priority: Priority;
  type: TaskType;
  assignee_id: string;
  pdm_id: string;
  parent_id: string;
  epic_id: string;
  source: string[];
  labels: string[];
  start_date: string;
  end_date: string;
};

const blankForm = (): Form => ({
  title: '',
  description: '',
  status: 'not_started',
  priority: DEFAULT_PRIORITY,
  type: DEFAULT_TASK_TYPE,
  assignee_id: '',
  pdm_id: '',
  parent_id: '',
  epic_id: '',
  source: [],
  labels: [],
  start_date: '',
  end_date: '',
});

const fromItem = (it: Item): Form => ({
  title: it.title,
  description: it.description,
  status: it.status,
  priority: it.priority ?? DEFAULT_PRIORITY,
  type: it.type ?? DEFAULT_TASK_TYPE,
  assignee_id: it.assignee_id ?? '',
  pdm_id: it.pdm_id ?? '',
  parent_id: it.parent_id ?? '',
  epic_id: it.epic_id ?? '',
  source: it.source ?? [],
  labels: it.labels ?? [],
  start_date: it.start_date?.slice(0, 10) ?? '',
  end_date: it.end_date?.slice(0, 10) ?? '',
});

/** The item fields sent on create/update, in a fixed key order so two bodies can be compared
 *  as JSON to skip a no-op save (see `sameBody`). */
const buildBody = (f: Form, boardId: string) => ({
  board_id: boardId,
  parent_id: f.parent_id || null,
  epic_id: f.epic_id || null,
  title: f.title.trim(),
  description: f.description,
  status: f.status,
  priority: f.priority,
  type: f.type,
  assignee_id: f.assignee_id || null,
  pdm_id: f.pdm_id || null,
  source: f.source,
  labels: f.labels,
  start_date: f.start_date || null,
  end_date: f.end_date || null,
});
const sameBody = (a: object, b: object) => JSON.stringify(a) === JSON.stringify(b);

// For a full-item PUT that should persist only the fields the user TOUCHED: take each field from
// `snap` where it differs from the loaded baseline `base` (a real edit), otherwise from the freshest
// live item `live`. This stops the autosave from reverting a peer's concurrent change to a field the
// user never touched (a naive full write would carry that field's stale, loaded value).
const mergeUntouched = (snap: Form, base: Form, live: Form): Form => {
  const out: Record<string, unknown> = { ...snap };
  const s = snap as Record<string, unknown>;
  const b = base as Record<string, unknown>;
  const l = live as Record<string, unknown>;
  for (const k of Object.keys(s)) {
    const touched = Array.isArray(s[k]) || Array.isArray(b[k]) ? JSON.stringify(s[k]) !== JSON.stringify(b[k]) : s[k] !== b[k];
    if (!touched) out[k] = l[k];
  }
  return out as unknown as Form;
};

/** Autosave status, surfaced by the footer indicator that replaces the old Save button. */
type SaveState = 'idle' | 'saving' | 'saved' | 'error';
// Debounce a field change this long before writing, so a typing burst coalesces into one save
// (and one undo entry / one board refresh). A close / item-switch / unmount flushes it early.
const AUTOSAVE_MS = 600;

/** A write captured explicitly at compose time so it survives an item switch (which repoints the
 *  drawer's refs). `draft` is the create-draft generation it was composed for, used to reject an
 *  adopt into a different new draft opened since (see runSave). */
type SaveCtx = {
  targetId: string | null;
  snap: Form;
  before: Item | null;
  board: string;
  draft: number;
};

/** Identity of a queued write, so repeated edits to the SAME item (or the same create draft)
 *  coalesce to their latest snapshot while writes to DIFFERENT items are each preserved. A create
 *  follow-up has no id yet, so it keys off its draft generation. */
const pendingKey = (c: SaveCtx) => c.targetId ?? `draft:${c.draft}`;

// A file dragged over the page re-fires `dragover` roughly every 350ms; this much silence means
// the drag is gone (dropped outside, dragged back to the desktop, or Escape-cancelled) — none of
// which fire a reliable event of their own. Ported from the owners-hub replace-logo dropzone.
const DRAG_IDLE_MS = 900;

/** Clipboard images usually arrive unnamed (an "image/png" blob with no filename) — give them a
 *  distinct one so successive pastes don't all collide on "pasted-image.png" in the list and in the
 *  download link. `i` disambiguates several images pasted in one go. */
function namePastedImage(file: File, i: number): File {
  if (file.name) return file;
  const ext = file.type === 'image/svg+xml' ? 'svg' : file.type.split('/')[1] || 'png';
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  return new File([file], `pasted-${stamp}${i ? `-${i + 1}` : ''}.${ext}`, { type: file.type });
}

/** A human message for a failed upload: the backend's `{"error": …}` when present, else a guess
 *  from the status (the API caps upload size and 409s when object storage is switched off). */
function uploadErrorMessage(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { error?: string } } };
  if (e?.response?.data?.error) return e.response.data.error;
  if (e?.response?.status === 413) return 'the file is too large';
  if (e?.response?.status === 409) return 'attachments are unavailable (storage is off)';
  return 'the upload failed';
}

/** One banner line from a batch's failures — keeping the per-file cause the single-file path shows.
 *  When every failure shares a cause (e.g. storage is off) it's stated once for the whole batch;
 *  otherwise the failed filenames are listed so the user can tell which ones to retry. */
function combineFailures(failed: { name: string; reason: string }[]): string {
  if (failed.length === 1) return `Couldn't upload ${failed[0].name} — ${failed[0].reason}`;
  const reasons = Array.from(new Set(failed.map((f) => f.reason)));
  if (reasons.length === 1) return `Couldn't upload ${failed.length} files — ${reasons[0]}`;
  return `Couldn't upload ${failed.length} files: ${failed.map((f) => f.name).join(', ')}`;
}

/** "Hide panel" glyph (|→): a bar with an arrow pointing toward it — collapses the empty dock away. */
const HidePanel = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M4 5v14" />
    <path d="M9 12h11" />
    <path d="M16 8l4 4-4 4" />
  </svg>
);

export default function ItemDrawer({
  open,
  onClose,
  itemId,
  boardId,
  onSaved,
  initialStatus,
  initialParentId,
  initialType,
  initialEpicId,
  initialTab,
  items = [],
  presentation = 'auto',
  highlight,
  openAttachment,
}: Props) {
  const { members, boards } = useAppData();
  const { me } = useIdentity();
  const openLightboxFn = useLightbox();
  const openedAttRef = useRef<string | null>(null);
  const { subscribeItems } = useRealtime();
  // Workspace-global label suggestions for the LabelsRow autocomplete (labels are shared across boards).
  const [labelOptions, setLabelOptions] = useState<string[]>([]);
  useEffect(() => {
    api.listLabels().then(setLabelOptions).catch(() => undefined);
  }, []);
  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const { record, showToast } = useHistory();
  const { statuses, defaultValue: defaultStatus } = useStatuses();
  const statusMetaOf = useStatusMeta();
  // Presentation only: whether to render as a docked side drawer vs. a modal, and the pin
  // toggle. The open/itemId/create-defaults still arrive as props (the views pass them from
  // the same panel context), so this component's data flow is unchanged.
  const { docked, dockVisible, hideEmpty, canDock, pinned, togglePinned, openItem, pushItem, back, stackDepth, dockWidth, setDockWidth } = useItemPanel();
  const navigate = useNavigate();
  const wc = useWatches();
  // Read the board's default status at create-time without re-running the reset effect (which
  // would wipe in-progress input) whenever the status set changes.
  const defaultStatusRef = useRef(defaultStatus);
  defaultStatusRef.current = defaultStatus;
  const [id, setId] = useState<string | null>(itemId);
  const [tab, setTab] = useState<Tab>('details');
  // In the drawer the property table starts collapsed to a one-line summary (status · assignee ·
  // end date), so description + attachments lead; "More details" expands the full editable table.
  // Reset per open: collapsed for an existing item, expanded for a fresh create (all fields to fill).
  const [showAllProps, setShowAllProps] = useState(false);
  const [form, setForm] = useState<Form>(blankForm());
  // Bumped on every genuine open/switch to re-key the content wrapper (replaying the panel-swap
  // animation). NOT bumped when an autosave create promotes the draft in place, so the just-created
  // form never remounts mid-edit and loses focus. See the reset effect.
  const [contentKey, setContentKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false); // GET /items/:id failed (e.g. deleted by a peer)
  const [deletedExternally, setDeletedExternally] = useState(false); // the OPEN item was removed by a peer
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const saveStateRef = useRef(saveState);
  saveStateRef.current = saveState;
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [links, setLinks] = useState<ItemLink[]>([]);
  const [meta, setMeta] = useState<Item | null>(null); // loaded/saved item, for created/updated display
  const [commentBody, setCommentBody] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null); // parent comment id an inline reply targets
  const [replyBody, setReplyBody] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadBtnRef = useRef<HTMLButtonElement>(null);

  // The drawer is a single reused instance (one per page, `itemId` swapped, never remounted), so an
  // upload that was started for one item can resolve after the drawer has moved to another. `idRef`
  // is the item on screen right now and `mountedRef` guards the (unlikely) unmount, so a late
  // continuation can tell whether its writes still belong on screen. See `aliveFor` below.
  const mountedRef = useRef(true);
  // Re-arm on setup, not just disarm on cleanup: StrictMode (dev) mounts → cleans up → remounts the
  // SAME fiber, so a cleanup that only sets `false` leaves the ref stuck `false` after remount — then
  // every guard below fails and `loading` never clears (infinite spinner). Setting `true` on setup
  // survives both the StrictMode remount and any genuine remount.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const idRef = useRef(id);
  idRef.current = id;
  const aliveFor = (forId: string) => mountedRef.current && idRef.current === forId;
  // Mirror the latest form/meta so a debounced (or flushed-on-switch) save reads the right
  // snapshot: the flush during an item switch runs one render before the reset commits, when
  // these still hold the OUTGOING item's values.
  const metaRef = useRef(meta);
  metaRef.current = meta;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const deletedRef = useRef(false);
  deletedRef.current = deletedExternally;
  const formRef = useRef(form);

  const memberName = useCallback(
    (mid: string | null) => members.find((m) => m.id === mid)?.name ?? null,
    [members],
  );

  const loadSub = useCallback(async (iid: string) => {
    const [at, cs, ac, lk] = await Promise.all([
      api.listAttachments(iid),
      api.listComments(iid),
      api.listActivity(iid),
      api.listLinks(iid),
    ]);
    // The reused drawer may have switched to another item (or unmounted) while these fetched — the
    // guard is inside loadSub so it protects EVERY caller (initial load, upload refresh, comment
    // add, delete), not just the ones that remember to check afterwards.
    if (!mountedRef.current || idRef.current !== iid) return;
    setAttachments(at);
    setComments(cs);
    setActivity(ac);
    setLinks(lk);
  }, []);

  // ---- Autosave -------------------------------------------------------------
  // Every field routes through `set`, which schedules a debounced write — there is no Save
  // button. `runSave` is serialized (a create must never fire twice) and every post-await write
  // is guarded by the same `idRef`/`mountedRef` checks the upload path uses, so a save that
  // resolves after the reused drawer has moved to another item can't paint onto it. The pending
  // edit is flushed the instant the drawer closes, switches item, or unmounts (see below).
  useEffect(() => {
    formRef.current = form;
  }, [form]);
  const saveTimer = useRef<number | undefined>(undefined);
  const savingRef = useRef(false); // a write is in flight — serialize so create never doubles
  // Writes captured while a save was in flight, to drain once it settles. Each holds the FULL
  // snapshot (not just a flag) so an item switch that repoints the refs mid-write can't misdirect
  // the follow-up onto the wrong item — and it's a QUEUE, coalesced by `pendingKey`, so two writes
  // pending at once (an outgoing item's edit + the incoming item's edit after a switch) can't
  // collapse onto one slot and drop the other.
  const pendingQueue = useRef<SaveCtx[]>([]);
  // Whether the drawer is currently open, read inside a create's post-await block so a create that
  // resolves after the user dismissed the draft doesn't re-open the panel.
  const openRef = useRef(open);
  openRef.current = open;
  // The create-draft generation: bumped each time a fresh create form opens (reset effect). A
  // create composed for generation N must not adopt if a different draft (N+1) is showing by the
  // time it resolves — otherwise it would rebind the new draft's text to the old item's id.
  const draftSeqRef = useRef(0);
  // The board a create draft was composed on, captured at open so switching boards mid-draft
  // doesn't file the new item under the board switched TO (an edit uses the item's own board).
  const createBoardRef = useRef(boardId);
  // Live board id, read without listing `boardId` in the reset effect's deps — an undo/redo can
  // switch boards under an open drawer, and a re-run of that effect would clobber in-progress edits.
  const boardIdRef = useRef(boardId);
  boardIdRef.current = boardId;
  // Set to a just-created id when the draft is promoted to a saved item (see runSave). The reset
  // effect consumes it once to skip re-loading an item this drawer already has in hand.
  const adoptedRef = useRef<string | null>(null);

  const runSave = useCallback(async (input?: SaveCtx) => {
    // Compose the write from the live refs unless an explicit snapshot was handed in (a deferred
    // follow-up or an outgoing-item flush).
    const ctx: SaveCtx = input ?? {
      targetId: idRef.current,
      snap: formRef.current,
      before: metaRef.current,
      board: metaRef.current ? metaRef.current.board_id : createBoardRef.current,
      draft: draftSeqRef.current,
    };
    if (savingRef.current) {
      // Defer behind the in-flight write, coalescing by identity so this target's latest snapshot
      // wins while other targets stay queued.
      const at = pendingQueue.current.findIndex((c) => pendingKey(c) === pendingKey(ctx));
      if (at >= 0) pendingQueue.current[at] = ctx;
      else pendingQueue.current.push(ctx);
      return;
    }
    // Take the save slot up front, so EVERY exit path below runs the finally that drains the next
    // queued write — the early-return guards must not strand the rest of the queue.
    savingRef.current = true;
    // Cancel the pending debounce ONLY on the fresh-compose path (a fired/re-armed timer for the
    // item on screen now). A drained follow-up (`input` provided) can target a DIFFERENT item than
    // the one currently shown, and must not cancel that item's live debounce — doing so would drop
    // its edit and leave its indicator stuck on "Saving…".
    if (!input) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = undefined;
    }

    const { targetId, snap, before, board, draft } = ctx;
    try {
      // Title is required, so an empty one is never persisted (neither create nor edit). Clear the
      // indicator so it doesn't hang on "Saving…"; the footer shows the "add a title" nudge.
      if (!snap.title.trim()) {
        if (mountedRef.current && idRef.current === targetId) setSaveState('idle');
        return;
      }
      // An edit whose item hasn't finished loading (or whose load failed) has no `before`; writing
      // now would PUT stale fields and skip the undo snapshot. (A create legitimately has none.)
      if (targetId && !before) {
        if (mountedRef.current && idRef.current === targetId) setSaveState('idle');
        return;
      }

      const body = buildBody(snap, board);
      // A change that lands back on the saved state (e.g. re-picking the current status) is a
      // no-op: skip it so it neither writes a bare "saved" activity entry nor reloads the board.
      if (targetId && before && sameBody(body, buildBody(fromItem(before), board))) {
        if (mountedRef.current && idRef.current === targetId) setSaveState('saved');
        return;
      }
      // The open item was deleted by a peer — don't PUT to a dead id (it would 404/500).
      if (targetId && targetId === idRef.current && deletedRef.current) {
        if (mountedRef.current) setSaveState('idle');
        return;
      }
      // For an EDIT, merge untouched fields from the freshest live item so this full PUT can't revert
      // a peer's concurrent change to a field the user never touched. (A create has no `before`/live.)
      const live = targetId ? itemsRef.current.find((i) => i.id === targetId) : undefined;
      const putBody = live && before ? buildBody(mergeUntouched(snap, fromItem(before), fromItem(live)), board) : body;

      if (mountedRef.current && idRef.current === targetId) setSaveState('saving');
      if (targetId) {
        const saved = await api.updateItem(targetId, putBody);
        // Undo target = the PRE-EDIT state: the user's original values for the fields they changed (from
        // `before`) + the freshest live values for the rest — so undoing this edit doesn't also revert a
        // peer's concurrent change to a field the user never touched.
        let undoBefore = before as Item;
        if (live && before) {
          const bf = fromItem(before) as Record<string, unknown>;
          const sf = snap as unknown as Record<string, unknown>;
          const lv = live as unknown as Record<string, unknown>;
          const ub = { ...(before as unknown as Record<string, unknown>) };
          for (const k of Object.keys(bf)) {
            const touched = Array.isArray(sf[k]) || Array.isArray(bf[k]) ? JSON.stringify(sf[k]) !== JSON.stringify(bf[k]) : sf[k] !== bf[k];
            if (!touched) ub[k] = lv[k];
          }
          undoBefore = ub as unknown as Item;
        }
        record(snapshotCommand(undoBefore, saved, `Edit ${(before as Item).key}`));
        onSaved();
        // Re-baseline any queued follow-up for THIS item to the state we just persisted, so its
        // no-op check and undo snapshot compare against current server truth, not a stale compose-
        // time `before` (else a revert made mid-save would be seen as a no-op and dropped).
        for (const c of pendingQueue.current) if (c.targetId === targetId) c.before = saved;
        if (mountedRef.current && idRef.current === targetId) {
          setMeta(saved);
          // Fold the saved (merged) values back into the FORM for fields the user hasn't further edited
          // since this save was composed — else a field the merge refreshed to a peer's value keeps its
          // stale form value, and a queued follow-up would re-send it, reverting the peer's change.
          setForm((f) => {
            const cur = f as unknown as Record<string, unknown>;
            const sf = snap as unknown as Record<string, unknown>;
            const sv = fromItem(saved) as Record<string, unknown>;
            const out = { ...cur };
            for (const k of Object.keys(out)) {
              // The rich-text editor owns `description`; re-setting its value resets the caret to the
              // end mid-typing, so never fold it back here (the editor is the source of truth for it).
              if (k === 'description') continue;
              const unchanged = Array.isArray(cur[k]) || Array.isArray(sf[k]) ? JSON.stringify(cur[k]) === JSON.stringify(sf[k]) : cur[k] === sf[k];
              if (unchanged) out[k] = sv[k];
            }
            return out as unknown as Form;
          });
          setSaveState('saved');
          // The write already succeeded — a failed sub-resource refetch must not flip 'saved' to
          // 'error' via the outer catch.
          await loadSub(targetId).catch(() => undefined);
        }
      } else {
        const created = await api.createItem(body);
        record(existenceCommand(created.id, [created.id], created.board_id, `New ${created.key}`));
        onSaved();
        // The item now exists — redirect any follow-up composed during this create (queued with a
        // null target) onto it, BEFORE adopting `idRef`, so it drains as an UPDATE: never a second
        // create, and never lost even if the user switched away before the create resolved. Doing
        // it pre-adopt means an edit made during the loadSub below coalesces onto the same key.
        for (const c of pendingQueue.current) {
          if (c.targetId === null && c.draft === draft) {
            c.targetId = created.id;
            c.before = created;
            c.board = created.board_id;
          }
        }
        // Adopt into the UI only if the drawer is still open on the SAME draft this write was
        // composed for — not closed (openRef), not switched to a saved item (idRef), and not a
        // different new draft opened since (draftSeqRef).
        const stillThisDraft =
          mountedRef.current && openRef.current && idRef.current === null && draftSeqRef.current === draft;
        if (stillThisDraft) {
          idRef.current = created.id;
          setId(created.id);
          setMeta(created);
          setSaveState('saved');
          await loadSub(created.id).catch(() => undefined);
          // Promote the draft to the saved item in the shared panel so its URL (?item=) and the
          // board's selection ring pick it up. `adoptedRef` tells the reset effect this incoming
          // itemId is already loaded here — so it skips the re-fetch/form reset that would
          // otherwise clobber the just-created form (see the reset effect below).
          adoptedRef.current = created.id;
          openItem(created.id);
        }
      }
    } catch {
      // Surface an error only if this write's surface is still on screen (its item, or the same
      // still-open draft for a create).
      const stillHere = mountedRef.current && (targetId ? idRef.current === targetId : idRef.current === null && openRef.current);
      if (stillHere) setSaveState('error');
    } finally {
      savingRef.current = false;
      const next = pendingQueue.current.shift(); // FIFO; each drain chains the rest via its finally
      if (next) void runSave(next);
    }
  }, [loadSub, onSaved, record, openItem]);

  const scheduleSave = useCallback(() => {
    // Reflect intent immediately (the footer indicator reads this); an empty title shows an
    // "add a title" nudge via the indicator's own precedence, and `runSave` no-ops it.
    setSaveState('saving');
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      void runSave();
    }, AUTOSAVE_MS);
  }, [runSave]);

  // Flush a pending debounced save at once — no-op if nothing is pending; if a write is already
  // in flight, `runSave` folds it into that write's follow-up. Only edits to an already-saved item
  // are flushed: a brand-new draft that hasn't auto-created yet is discarded on close/switch rather
  // than silently created (✕ = cancel for a new item; auto-create happens only while you're editing,
  // once the debounce settles).
  const flush = useCallback(() => {
    if (saveTimer.current === undefined) return;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = undefined;
    if (idRef.current === null) return;
    void runSave();
  }, [runSave]);
  const flushRef = useRef(flush);
  flushRef.current = flush;
  // Backstop for a genuine unmount (route change, provider teardown). Empty deps so the cleanup
  // runs only on unmount; StrictMode's dev mount/unmount/mount finds nothing pending.
  useEffect(() => () => flushRef.current(), []);

  useEffect(() => {
    // Persist any pending field edit for the OUTGOING item before we move on — a change made
    // inside the debounce window would otherwise be lost on a close or an item switch. Reads the
    // refs, which still hold the outgoing item here (the reset below commits next render).
    flushRef.current();
    if (!open) return;
    // The draft we just created was promoted to this itemId (runSave → openItem): the item is
    // already loaded here, so consume the marker and skip the reset — re-fetching would wipe the
    // freshly-created form and flash a spinner mid-edit.
    if (itemId && itemId === adoptedRef.current) {
      adoptedRef.current = null;
      return;
    }
    // A genuine open/switch (not an in-place adopt) — re-key the content so the panel-swap
    // animation replays, and drop stale autosave bookkeeping before loading the new item.
    setContentKey((k) => k + 1);
    adoptedRef.current = null; // drop any adopt marker that was never consumed (e.g. a fast switch)
    // Fresh item → drop the outgoing item's debounce timer. NOTE: `pendingQueue` is intentionally
    // NOT cleared — an in-flight save for the outgoing item owns it and drains it on settle, so
    // clearing here would silently drop a queued edit made just before the switch.
    window.clearTimeout(saveTimer.current);
    saveTimer.current = undefined;
    setSaveState('idle');
    setLoadError(false);
    setId(itemId);
    idRef.current = itemId; // keep the "current item" guard correct before setId commits
    // Deep-link to the requested tab, but only for a saved item — Comments/Activity are
    // disabled in create mode (their sub-resources don't exist yet), so a create always
    // opens on Details regardless of initialTab.
    setTab(itemId ? initialTab ?? 'details' : 'details');
    // Existing item → lead with the collapsed summary; a fresh create → expand so every field is
    // there to fill in. (An in-place adopt returns above, so a just-created draft stays expanded.)
    setShowAllProps(!itemId);
    setCommentBody('');
    setAttachments([]);
    setComments([]);
    setActivity([]);
    setLinks([]);
    setMeta(null);
    setConfirmDelete(false);
    // Upload UI is per-item too — don't let one item's error banner / spinner / drop veil bleed
    // onto the next one the reused drawer is opened for.
    setUploading(false);
    setUploadError(null);
    setDragging(false);
    // Sub-task tree UI is per-item too — drop a pending optimistic reorder, the collapse state, and
    // the create-modal target so a switch to the next item never renders (or re-targets a drag at)
    // the previous item's sub-tasks.
    setLocalSubs(null);
    setCollapsedSubs(new Set());
    setNewSubOpen(false);
    setCreateParent(null);
    setDeletedExternally(false);
    openedAttRef.current = null; // let a new item's ?att deep link fire
    if (itemId) {
      setLoading(true);
      (async () => {
        try {
          const it = await api.getItem(itemId);
          // A fast switch to another item can resolve this GET after the drawer has moved on — don't
          // stamp the previous item's fields over the one now loading.
          if (!mountedRef.current || idRef.current !== itemId) return;
          setForm(fromItem(it));
          setMeta(it);
          await loadSub(itemId);
        } catch {
          // The item is gone (a peer deleted it) or the network failed. Show an error instead of a
          // blank, editable form — without `meta` an autosave can't run, so an edit here would
          // otherwise stick on "Saving…" forever with nothing persisted.
          if (mountedRef.current && idRef.current === itemId) setLoadError(true);
        } finally {
          if (mountedRef.current && idRef.current === itemId) setLoading(false);
        }
      })();
    } else {
      // A fresh create draft: bump the generation and capture the board it's composed on, so a
      // slow create can tell whether it still belongs to THIS draft / board when it resolves.
      draftSeqRef.current += 1;
      createBoardRef.current = boardIdRef.current;
      setForm({
        ...blankForm(),
        status: initialStatus ?? defaultStatusRef.current,
        parent_id: initialParentId ?? '',
        // Top-level items default to "story"; a sub-task (created under a parent) defaults to "task".
        type: (initialType as TaskType) ?? (initialParentId ? 'task' : 'story'),
        // Adopt the focused epic when creating from a filtered epic view — but never for an
        // epic-typed item itself (epics carry no epic link).
        epic_id: initialType === 'epic' ? '' : initialEpicId ?? '',
      });
    }
  }, [open, itemId, loadSub, initialStatus, initialParentId, initialType, initialEpicId, initialTab]);

  // Deep-link + flash: opened on a `highlight` target (from a notification click), switch to the tab
  // that holds it, then — once it has rendered (comments load async) — scroll it into view and run
  // the `.hl-flash` animation. Runs AFTER the reset effect above so its tab choice wins. Poll briefly
  // because the target element (a comment, a freshly-loaded field) may not be in the DOM yet.
  useEffect(() => {
    if (!open || !highlight) return undefined;
    setTab(highlight.startsWith('comment') ? 'comments' : 'details');
    let tries = 0;
    let timer = window.setTimeout(function find() {
      const el = document.querySelector<HTMLElement>(`[data-hl="${highlight}"]`);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.classList.remove('hl-flash');
        void el.offsetWidth; // force reflow so the animation restarts if the same target is re-hit
        el.classList.add('hl-flash');
        window.setTimeout(() => el.classList.remove('hl-flash'), 6200);
        return;
      }
      if (tries++ < 40) timer = window.setTimeout(find, 100); // retry up to ~4s while content loads
    }, 120);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, highlight, itemId]);

  // Reconcile the epic link from the live items list when it changes underneath the open drawer —
  // e.g. an epic dragged from the sidebar onto this item's row calls the narrow setItemEpic, whose
  // realtime echo updates the page's items but not this form. Without this, the drawer's next
  // full-replace autosave (buildBody carries epic_id) would clobber that just-linked epic back to
  // null. Adopt the live value only when the user hasn't themselves re-picked the epic here.
  useEffect(() => {
    if (!itemId) return;
    const live = items.find((i) => i.id === itemId);
    const m = metaRef.current;
    if (!live || !m || (live.epic_id ?? null) === (m.epic_id ?? null)) return;
    const known = m.epic_id ?? '';
    if ((formRef.current.epic_id ?? '') === known) {
      setForm((f) => ({ ...f, epic_id: live.epic_id ?? '' }));
    }
    setMeta((prev) => (prev ? { ...prev, epic_id: live.epic_id ?? null } : prev));
  }, [items, itemId]);

  // Keep the OPEN item's fields live. Another tab/user editing it — or the SAME user editing it from
  // the board/table — updates the page's `items` feed, but the drawer holds its own loaded snapshot
  // (form/meta), so without this you'd have to refresh to see the change. Adopt the live snapshot, but
  // ONLY while the form has no unsaved edits and no save is in flight, so we never clobber what's being
  // typed. (The narrow epic reconcile above additionally covers the epic-drag-mid-edit case.)
  useEffect(() => {
    if (!itemId) return;
    const live = items.find((i) => i.id === itemId);
    const m = metaRef.current;
    if (!live || !m) return;
    if (m.id !== itemId) return; // meta still holds the PREVIOUS item (mid-switch) — wait for its load
    // Don't adopt a STALER snapshot than our baseline: right after our own save the board list is
    // momentarily pre-reload (older updated_at); adopting it would revert the just-saved text and reset
    // the description caret. Only a genuinely newer (peer) change is adopted.
    if (live.updated_at && m.updated_at && live.updated_at < m.updated_at) return;
    if (saveStateRef.current === 'saving') return; // a save is pending/in flight → let it settle first
    const board = boardIdRef.current ?? live.board_id;
    const baseBody = buildBody(fromItem(m), board);
    if (sameBody(buildBody(fromItem(live), board), baseBody)) return; // live already matches our baseline
    if (!sameBody(buildBody(formRef.current, board), baseBody)) return; // unsaved local edits → keep them
    setForm(fromItem(live));
    setMeta(live);
    // saveState is a dep so this re-runs when a save settles (a no-op or errored save produces no new
    // `items` ref), otherwise a peer edit made during that save could stay stale until the next event.
  }, [items, itemId, saveState]);

  // Live delete + sub-resource handling off the item feed. A peer DELETING this item surfaces the
  // deleted state — from the EXPLICIT delete event (precise), not inferred from list absence (which a
  // paging/reconnect window could false-positive on a large board). A peer changing this item's fields
  // OR its comments/attachments (item.upsert — including the re-broadcast the backend sends on a
  // comment/attachment change) pulls comments/activity/attachments fresh; own-tab echoes are skipped
  // (we already reload locally on our own comment/upload).
  useEffect(() => {
    if (!id) return undefined;
    return subscribeItems((ev) => {
      if (ev.type === 'item.delete') {
        if (((ev.payload as { ids?: string[] })?.ids ?? []).includes(id)) setDeletedExternally(true);
        return;
      }
      if (ev.client_id && ev.client_id === CLIENT_ID) return;
      if (ev.type === 'item.upsert' && (ev.payload as Item)?.id === id) void loadSub(id);
    });
  }, [id, subscribeItems, loadSub]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    scheduleSave();
  };

  // Changing the type to Epic clears any epic link — an epic can't belong to another epic. This
  // also closes the edge case where a create form seeded with the rail's focused epic is then
  // switched to type=Epic (which would otherwise persist an epic linked to another epic).
  const setType = (v: TaskType) => {
    setForm((f) => ({ ...f, type: v, ...(v === 'epic' ? { epic_id: '' } : {}) }));
    scheduleSave();
  };

  // Create a new epic in-place from the Epic picker and link this item to it. The epic is a real
  // server item (type=epic); linking is just set('epic_id'), which the autosave persists. onSaved
  // reloads the page so the new epic shows in the picker options, the epic sidebar and the views.
  const createEpicInline = async (title: string) => {
    try {
      const created = await api.createItem({ board_id: boardIdRef.current, title: title.trim() || 'New epic', type: 'epic' });
      set('epic_id', created.id);
      onSaved();
    } catch {
      /* best-effort — the picker just won't gain the new epic */
    }
  };

  const doDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      const res = await api.deleteItem(id);
      record(removalCommand(id, res.ids ?? [id], meta?.board_id ?? boardId, `Delete ${meta?.key ?? 'item'}`));
      onSaved();
      setConfirmDelete(false);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  const addComment = async () => {
    if (!id || !commentBody.trim()) return;
    await api.createComment(id, { body: commentBody.trim() });
    setCommentBody('');
    await loadSub(id);
    // Refresh the list behind the drawer so the item's comment_count (and its "has comments"
    // dot) reflects the new comment right away, rather than only after the next natural reload.
    onSaved();
  };

  // Post a threaded reply (parent_id links it under the comment it answers).
  const sendReply = async (parentId: string) => {
    if (!id || !replyBody.trim()) return;
    await api.createComment(id, { body: replyBody.trim(), parent_id: parentId });
    setReplyBody('');
    setReplyTo(null);
    await loadSub(id);
    onSaved();
  };

  // Every upload source — the picker, a drop, a paste — funnels through here. Uploads run
  // sequentially so a failure names the file that broke while the rest still land. The target id is
  // snapshotted and every post-await write is guarded by `aliveFor`, so a slow upload can never
  // paint a previous item's list / spinner / error onto whatever item the reused drawer has since
  // moved to. `inFlight` tracks overlapping batches (a paste then a quick drop) so only a truly
  // fresh batch clears a standing error and only the last to finish drops the spinner.
  const inFlight = useRef(0);
  const uploadFiles = useCallback(
    async (files: File[]) => {
      const forId = idRef.current;
      if (!forId || files.length === 0) return;
      const freshBatch = inFlight.current === 0;
      inFlight.current += 1;
      if (aliveFor(forId)) {
        if (freshBatch) setUploadError(null);
        setUploading(true);
      }
      const failed: { name: string; reason: string }[] = [];
      for (const file of files) {
        try {
          await api.uploadAttachment(forId, file);
        } catch (err) {
          failed.push({ name: file.name || 'file', reason: uploadErrorMessage(err) });
        }
      }
      inFlight.current -= 1;
      if (!aliveFor(forId)) return; // the drawer moved to another item — leave it untouched
      await loadSub(forId).catch(() => undefined);
      if (!aliveFor(forId)) return;
      if (inFlight.current === 0) setUploading(false); // only the last batch clears the spinner
      if (failed.length) setUploadError(combineFailures(failed));
    },
    [loadSub],
  );

  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (fileRef.current) fileRef.current.value = ''; // let the same file be picked again
    void uploadFiles(files);
  };

  // The long-lived window listeners below read the latest upload fn and accept-state through refs
  // rather than re-subscribing on every change.
  const uploadRef = useRef(uploadFiles);
  uploadRef.current = uploadFiles;
  // A drop/paste attaches only to a SAVED item that is the live top surface — not while the delete
  // confirmation is stacked over the drawer (a stray paste there would attach to the item about to
  // be deleted), and not while the item failed to load (the error state hides the attachment UI, so
  // an upload would fire invisibly against a possibly-deleted item). The navigation guard below is
  // independent of this.
  const canAccept = !!id && !confirmDelete && !loadError && !deletedExternally;
  const canAcceptRef = useRef(canAccept);
  canAcceptRef.current = canAccept;

  // Drag-and-drop + clipboard paste — armed across the whole window while the drawer is open, so a
  // file dropped anywhere over the page, or an image pasted with any field focused, attaches. Ported
  // from the owners-hub casino-settings "replace logo" dropzone: the drag is tracked by a `dragover`
  // heartbeat + idle timeout rather than by counting dragenter/dragleave (which the overlay swapping
  // its own DOM mid-drag would break). `carriesFile` keeps element drags — the board's own @dnd-kit
  // reordering, a dragged link — from ever arming it.
  //
  // `preventDefault` on dragover/drop is UNCONDITIONAL while open: without it the browser navigates
  // to a dropped file, unloading the SPA and discarding an unsaved new item. Only the veil and the
  // upload itself wait for a saved, uncovered item (`canAccept`).
  useEffect(() => {
    if (!open) return undefined;

    const carriesFile = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    let idle: number | undefined;
    const disarm = () => {
      window.clearTimeout(idle);
      idle = undefined;
      setDragging(false);
    };
    const keepAlive = () => {
      window.clearTimeout(idle);
      idle = window.setTimeout(() => setDragging(false), DRAG_IDLE_MS);
    };
    const onOver = (e: DragEvent) => {
      if (!carriesFile(e)) return;
      e.preventDefault(); // else the browser navigates to the dropped file
      if (!canAcceptRef.current) return;
      setDragging(true);
      keepAlive();
    };
    const onLeave = (e: DragEvent) => {
      // A null relatedTarget means the pointer left the window itself — react at once rather than
      // waiting out the heartbeat.
      if (carriesFile(e) && e.relatedTarget === null) disarm();
    };
    const onDrop = (e: DragEvent) => {
      if (!carriesFile(e)) return;
      // The description editor claimed this drop already (its handleDrop uploads + embeds and
      // preventDefault'd) — don't ALSO attach it here, or the file uploads twice. Still disarm the veil.
      if (e.defaultPrevented) {
        disarm();
        return;
      }
      e.preventDefault();
      disarm();
      if (!canAcceptRef.current) return;
      void uploadRef.current(Array.from(e.dataTransfer?.files ?? []));
    };
    const onPaste = (e: ClipboardEvent) => {
      // The description editor (or any field) already handled this paste — its handlePaste embeds the
      // image and preventDefault's, so bailing here stops the same file uploading a second time.
      if (e.defaultPrevented) return;
      // Only claim a paste that carries image data, and only when we can actually take it; a
      // plain-text paste (or any paste in create mode) must still reach the focused field.
      if (!canAcceptRef.current) return;
      const images = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
      if (images.length === 0) return;
      e.preventDefault();
      void uploadRef.current(images.map(namePastedImage));
    };

    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragend', disarm);
    window.addEventListener('paste', onPaste);
    return () => {
      window.clearTimeout(idle);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragend', disarm);
      window.removeEventListener('paste', onPaste);
      setDragging(false);
    };
  }, [open]);

  const removeAttachment = async (aid: string) => {
    await api.deleteAttachment(aid);
    if (id) await loadSub(id);
    // The deleted row (and the button that had focus) is gone — pull focus to the Upload button so
    // it doesn't fall back to <body>, losing the keyboard user's place in the dialog.
    uploadBtnRef.current?.focus();
  };

  const sourceValues = Array.from(new Set([...SOURCES, ...form.source]));
  const labelValues = Array.from(new Set([...labelOptions, ...form.labels]));
  const activeMembers = [...members].sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));

  // Valid parent choices: any item except this one and its own descendants (no cycles).
  const descendants = useMemo(() => {
    const kids = new Map<string, string[]>();
    items.forEach((it) => {
      if (it.parent_id) {
        const arr = kids.get(it.parent_id) ?? [];
        arr.push(it.id);
        kids.set(it.parent_id, arr);
      }
    });
    const out = new Set<string>();
    const walk = (x: string) => (kids.get(x) ?? []).forEach((k) => {
      if (!out.has(k)) {
        out.add(k);
        walk(k);
      }
    });
    if (id) walk(id);
    return out;
  }, [items, id]);
  const parentOptions = items
    .filter((it) => it.id !== id && !descendants.has(it.id))
    .sort((a, b) => a.title.localeCompare(b.title));
  // Epic-link candidates: the board's Epic-typed items (excluding this one). Independent of the
  // parent tree, so — unlike parentOptions — no descendant exclusion, only self-exclusion.
  const epicOptions = items
    .filter((it) => it.type === 'epic' && it.id !== id)
    .sort((a, b) => a.title.localeCompare(b.title));

  // Sub-resources (comments, attachments, activity) only exist once the item is saved, so their
  // tabs stay disabled in create mode.
  const TABS: { key: Tab; label: string; count?: number; disabled?: boolean }[] = [
    { key: 'details', label: 'Details' },
    { key: 'comments', label: 'Comments', count: id ? comments.length : undefined, disabled: !id },
    { key: 'activity', label: 'Activity', disabled: !id },
    { key: 'stats', label: 'Stats', disabled: !id },
  ];

  // Reconstruct time-in-status + time-to-complete from the activity trail (for the Stats tab).
  const statsTimeline = useMemo(
    () => (id && meta ? buildStatusTimeline(activity, { created_at: meta.created_at, status: form.status }, statuses) : null),
    [id, meta, activity, form.status, statuses],
  );

  // Slug of the item's board + the full-page link (coexist: quick drawer → full task route).
  const itemBoardSlug = boards.find((b) => b.id === boardIdRef.current)?.slug;
  const fullPageHref = id && meta?.key && itemBoardSlug ? taskPath(itemBoardSlug, meta.key) : null;

  // Deep link `?att=<id>`: once this item's attachments have loaded, pop the lightbox on the shared
  // attachment (fires once per id). Lets a copied lightbox link open straight to that attachment.
  useEffect(() => {
    if (!openAttachment || !openLightboxFn) return;
    if (openedAttRef.current === openAttachment) return;
    if (!attachments.some((a) => a.id === openAttachment)) return; // not loaded yet / not on this item
    openedAttRef.current = openAttachment;
    openLightboxFn(attachments, openAttachment, fullPageHref ?? undefined);
  }, [openAttachment, attachments, openLightboxFn, fullPageHref]);

  const iconBtn = 'flex h-9 w-9 items-center justify-center rounded-full text-fg-faint transition-colors hover:bg-layer-strong hover:text-fg';

  // Shareable link to this item. The full-screen page links to its own route (/:slug/:key); the quick
  // drawer/modal links back to the VIEW it's floating over (the current path) with `?item=<key>`, so
  // the recipient lands on the same view with the drawer open + the item centered (see useItemUrlSync).
  const shareHref =
    presentation === 'page'
      ? fullPageHref
      : id && meta?.key
        ? `${window.location.pathname}?${new URLSearchParams({ [ITEM_PARAM]: meta.key }).toString()}`
        : null;

  // Copy the shareable link — a small button beside the title (kept OUTSIDE the title link so clicking
  // it copies rather than navigating).
  const copyItemLink = () => {
    const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!shareHref || !clip) return;
    clip.writeText(`${window.location.origin}${shareHref}`).then(
      () => showToast('Link copied'),
      () => showToast('Couldn’t copy the link', 'error'),
    );
  };
  const copyLinkBtn = shareHref ? (
    // Sits in the header's control row (as `headerAccessory`), so it lines up with — and matches the
    // size of — the close button next to it, rather than floating on the title's second line.
    <button
      type="button"
      onClick={copyItemLink}
      aria-label="Copy link to this item"
      title="Copy link"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-fg-faint transition-colors hover:bg-layer-strong hover:text-fg"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    </button>
  ) : null;

  // Full-page ONLY: a "Copy link" pill that sits next to Watch in the page's action row (the drawer
  // already exposes copy-link as the round header accessory near its close button, so this avoids a
  // duplicate there). Matches the Watch pill's shape/size so the two read as a pair.
  const copyLinkPill = fullPageHref ? (
    <button
      type="button"
      onClick={copyItemLink}
      title="Copy link to this item"
      className="flex h-9 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-medium text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
      Copy link
    </button>
  ) : null;

  // The header title. When the item has a full page, the WHOLE block is a link that opens it in a
  // new tab (a trailing external-link glyph marks it); otherwise a plain heading.
  const titleInner = (
    <>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">{id ? 'Edit item' : 'New item'}</div>
      <div className="mt-0.5 flex items-center gap-1.5 text-[15px] font-semibold text-fg-strong">
        <span className="truncate">{form.title || 'Untitled'}</span>
        {fullPageHref ? (
          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-fg-ghost transition-colors group-hover/title:text-fg-faint" />
        ) : null}
      </div>
    </>
  );
  // A Back affordance appears when this drawer was opened ON TOP of another (a sub-task drilled into
  // from within the panel), so you can pop back to the item that opened it.
  const canGoBack = stackDepth > 1 && presentation !== 'page';
  const titleLink = fullPageHref ? (
    <a
      href={fullPageHref}
      target="_blank"
      rel="noreferrer"
      title="Open as full page (new tab)"
      className="group/title -mx-1.5 block min-w-0 rounded-lg px-1.5 py-0.5 transition-colors hover:bg-layer"
    >
      {titleInner}
    </a>
  ) : (
    <div className="min-w-0">{titleInner}</div>
  );
  const title = canGoBack ? (
    <div className="flex min-w-0 items-center gap-1.5">
      <button
        type="button"
        onClick={back}
        aria-label="Back"
        title="Back"
        className="-ml-1.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-fg-faint transition-colors hover:bg-layer-strong hover:text-fg"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 6l-6 6 6 6" />
        </svg>
      </button>
      <div className="min-w-0 flex-1">{titleLink}</div>
    </div>
  ) : (
    titleLink
  );

  // Autosave replaces the Save/Close buttons (both surfaces already carry their own ✕): the footer
  // holds the dock/detach toggle, a live save-status indicator, and Delete (in a kebab). It
  // collapses entirely in a pristine create form (nothing to delete, nothing saved yet).
  const titleEmpty = !form.title.trim();
  const showFooter = !loadError && !deletedExternally && (!!id || saveState !== 'idle' || !titleEmpty);

  // Dock ↔ detach toggle — lives in the footer's lower-left. Absent on narrow screens (no docking)
  // and on the full page (nothing to detach).
  const detachBtn =
    canDock && presentation !== 'page' ? (
      <button
        type="button"
        onClick={togglePinned}
        aria-pressed={pinned}
        aria-label={pinned ? 'Detach to a floating dialog' : 'Dock as a side panel'}
        title={pinned ? 'Detach to a dialog' : 'Dock as a side panel'}
        className={iconBtn}
      >
        {pinned ? <Detach /> : <OpenPanel />}
      </button>
    ) : null;

  const watchBtn =
    id && wc ? (
      <button
        type="button"
        onClick={() => wc.toggle('item', id)}
        aria-pressed={wc.isWatching('item', id)}
        title={wc.isWatching('item', id) ? 'Stop watching this item' : 'Watch this item for updates'}
        className={cn(
          'flex h-9 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-medium transition-colors',
          wc.isWatching('item', id) ? 'bg-accent/15 text-accent' : 'text-fg-muted hover:bg-layer-strong hover:text-fg',
        )}
      >
        <Eye className="h-4 w-4" />
        {wc.isWatching('item', id) ? 'Watching' : 'Watch'}
      </button>
    ) : null;

  const footer = showFooter ? (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">{detachBtn}{watchBtn}{presentation === 'page' ? copyLinkPill : null}</div>
      <div className="flex items-center gap-2">
        <SaveIndicator
          state={saveState}
          titleEmpty={titleEmpty}
          onRetry={() => {
            window.clearTimeout(saveTimer.current);
            saveTimer.current = undefined;
            void runSave();
          }}
        />
        {/* Destructive action tucked behind a kebab so it isn't a prominent target. */}
        {id ? (
          <Popover
            portal
            align="right"
            trigger={({ open, toggle }) => (
              <button
                type="button"
                onClick={toggle}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="More actions"
                title="More actions"
                className="flex h-9 w-9 items-center justify-center rounded-full text-fg-faint transition-colors hover:bg-layer-strong hover:text-fg"
              >
                <MoreVertical />
              </button>
            )}
          >
            {(close) => (
              <button
                type="button"
                onClick={() => {
                  close();
                  setConfirmDelete(true);
                }}
                disabled={saveState === 'saving'}
                className={cn(menuRow, 'min-w-[150px] text-danger disabled:opacity-40')}
              >
                <Trash className="h-4 w-4" />
                <span className="flex-1">Delete item</span>
              </button>
            )}
          </Popover>
        ) : null}
      </div>
    </div>
  ) : undefined;

  // In the full-page presentation the Details tab reflows into two columns (wide description +
  // attachments on the left, properties on the right), using the extra real estate. These blocks
  // are extracted so both layouts reuse the exact same controls.
  const isPage = presentation === 'page';

  // On a very wide screen the full page gains a THIRD column: comments live to the right, always
  // visible, rather than behind a tab. Matches at ≥1440px; the docked drawer is never wide enough,
  // so this is gated on `isPage`. `effectiveTab` folds the (now-hidden) Comments tab back to Details.
  const [wideScreen, setWideScreen] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1440px)').matches);
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1440px)');
    const onChange = () => setWideScreen(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  const wideComments = isPage && wideScreen;
  const effectiveTab: Tab = wideComments && tab === 'comments' ? 'details' : tab;

  // The full property table. Used verbatim by the page's right rail and, in the drawer, revealed
  // by "More details" (see `propsSection`).
  // Comments — extracted so the same list + composer renders either as its own tab (narrow) or in
  // the wide page's right rail. Thread-folds the flat list one level deep, inline composer at the foot.
  const commentsBlock = (
    <>
      <div className="space-y-2">
        {(() => {
          const ids = new Set(comments.map((c) => c.id));
          const byParent = new Map<string, Comment[]>();
          const roots: Comment[] = [];
          for (const c of comments) {
            const root = c.parent_id && ids.has(c.parent_id) ? c.parent_id : null;
            if (root) {
              const arr = byParent.get(root) ?? [];
              arr.push(c);
              byParent.set(root, arr);
            } else {
              roots.push(c);
            }
          }
          const onChange = (u: Comment) => setComments((cs) => cs.map((x) => (x.id === u.id ? u : x)));
          const onDelete = (cid: string) => {
            setComments((cs) => cs.filter((x) => x.id !== cid));
            api.deleteComment(cid).catch(() => {
              if (id) void loadSub(id);
            });
          };
          const replyBox = (parentId: string) => (
            <div className="mt-1.5">
              <MentionInput value={replyBody} onChange={setReplyBody} members={activeMembers} onSubmit={() => sendReply(parentId)} placeholder="Reply… (@ to mention)" />
              <div className="mt-1 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setReplyTo(null); setReplyBody(''); }}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={() => sendReply(parentId)} disabled={!replyBody.trim()}>Reply</Button>
              </div>
            </div>
          );
          const openReply = (parentId: string) => { setReplyTo(parentId); setReplyBody(''); };
          return roots.map((c) => {
            const replies = byParent.get(c.id) ?? [];
            const showThread = replies.length > 0 || replyTo === c.id;
            return (
              <div key={c.id} data-hl={`comment:${c.id}`}>
                <CommentRow comment={c} members={activeMembers} memberById={memberById} me={me} onChange={onChange} onDelete={() => onDelete(c.id)} onReply={() => openReply(c.id)} />
                {showThread ? (
                  <div className="ml-[26px] mt-1 space-y-1 border-l border-line pl-3">
                    {replies.map((r) => (
                      <div key={r.id} data-hl={`comment:${r.id}`}>
                        <CommentRow comment={r} members={activeMembers} memberById={memberById} me={me} compact onChange={onChange} onDelete={() => onDelete(r.id)} onReply={() => openReply(c.id)} />
                      </div>
                    ))}
                    {replyTo === c.id ? replyBox(c.id) : null}
                  </div>
                ) : null}
              </div>
            );
          });
        })()}
        {comments.length === 0 ? <div className="text-[12px] text-fg-faint">No comments yet.</div> : null}
      </div>
      <div className="mt-3 flex items-start gap-2">
        <MentionInput value={commentBody} onChange={setCommentBody} members={activeMembers} onSubmit={addComment} placeholder="Write a comment… (@ to mention)" />
        <Button variant="primary" onClick={addComment} disabled={!commentBody.trim()}>
          Comment
        </Button>
      </div>
    </>
  );

  // Who created the item (read-only) — a row in the full table, and a quiet line beside the collapsed
  // summary so it's visible without expanding "More details".
  const reporter = meta?.created_by ? memberById.get(meta.created_by) : undefined;

  const propsTable = (
    <div className="overflow-hidden rounded-xl border border-line" data-hl="properties">
      <TypeRow value={form.type} onChange={setType} />
      <StatusRow value={form.status} meta={statusMetaOf(form.status)} statuses={statuses} onChange={(v) => set('status', v)} hlId="field:status" />
      <PriorityRow value={form.priority} onChange={(v) => set('priority', v)} hlId="field:priority" />
      <MemberRow label="Assignee" value={form.assignee_id} members={activeMembers} emptyLabel="Unassigned" onChange={(v) => set('assignee_id', v)} hlId="field:assignee" />
      <MemberRow label="PdM" value={form.pdm_id} members={activeMembers} emptyLabel="No PdM" noun="PdM" onChange={(v) => set('pdm_id', v)} />
      {meta ? <ReporterRow member={reporter} createdAgo={timeAgo(meta.created_at)} createdTitle={fmtDateTime(meta.created_at)} /> : null}
      <DateRow label="Start" heading="Start date" value={form.start_date} onChange={(v) => set('start_date', v)} />
      <DateRow label="End" heading="End date" align="right" value={form.end_date} onChange={(v) => set('end_date', v)} />
      <ParentRow value={form.parent_id} options={parentOptions} statusColor={(s) => statusMetaOf(s).color} onChange={(v) => set('parent_id', v)} />
      <EpicRow value={form.epic_id} options={epicOptions} onChange={(v) => set('epic_id', v)} onCreate={createEpicInline} />
      <SourceRow value={form.source} options={sourceValues} onChange={(v) => set('source', v)} />
      <LabelsRow value={form.labels} options={labelValues} onChange={(v) => set('labels', v)} />
    </div>
  );

  // The drawer's collapsible properties: the compact summary row at rest, the full table once
  // expanded, with a toggle between the two. The full page skips this (its roomy right rail always
  // shows the whole table).
  const propsSection = (
    <div className="mb-5">
      {showAllProps ? (
        propsTable
      ) : (
        <PropertySummary
          status={form.status}
          statusMeta={statusMetaOf(form.status)}
          statuses={statuses}
          onStatus={(v) => set('status', v)}
          assigneeId={form.assignee_id}
          members={activeMembers}
          onAssignee={(v) => set('assignee_id', v)}
          endDate={form.end_date}
          onEndDate={(v) => set('end_date', v)}
        />
      )}
      <div className="mt-2 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setShowAllProps((v) => !v)}
          aria-expanded={showAllProps}
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-1 py-0.5 text-[12px] font-medium text-fg-faint transition-colors hover:text-fg"
        >
          {showAllProps ? 'Show less' : 'More details'}
          <ChevronDown open={showAllProps} className="text-fg-ghost" />
        </button>
        {/* Expanded, the table's Reporter row says the same — so only the collapsed view shows this. */}
        {!showAllProps && meta ? (
          <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-fg-faint" title={`Created ${fmtDateTime(meta.created_at)}`}>
            <span className="shrink-0">Created by</span>
            <MemberAvatar member={reporter} size={16} className="shrink-0" />
            <span className="truncate font-medium text-fg-muted">{reporter ? reporter.name : 'Unknown'}</span>
            <span className="shrink-0 text-fg-ghost">· {timeAgo(meta.created_at)}</span>
          </span>
        ) : null}
      </div>
    </div>
  );

  const descBlock = (
    <Field label="Description">
      <MarkdownEditor
        value={form.description}
        onChange={(v) => set('description', v)}
        itemId={id}
        mediaAttachments={attachments.filter((a) => a.content_type?.startsWith('image/') || a.content_type?.startsWith('video/'))}
        onImageUploaded={() => id && loadSub(id)}
        placeholder="Details, acceptance criteria, links… (paste or drop images & videos)"
      />
    </Field>
  );

  const attachBlock = id ? (
    <section className={cn('border-t border-line pt-4', isPage ? 'mt-6' : 'mt-5')} data-hl="attachments">
      <div className="mb-1 flex items-center justify-between gap-2">
        <Label>Attachments ({attachments.length})</Label>
        <Button ref={uploadBtnRef} variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
          Upload
        </Button>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={onPickFiles} />
      </div>
      <p className="mb-2 text-[11px] text-fg-faint">Drag files onto the window, or paste an image, to attach.</p>
      {/* On the full page there's room to lay attachments out in a grid rather than a single stack. */}
      <div className={cn(isPage ? 'grid grid-cols-1 gap-1.5 sm:grid-cols-2' : 'space-y-1.5')}>
        {attachments.map((a) => (
          <AttachmentItem key={a.id} att={a} attachments={attachments} onDelete={() => removeAttachment(a.id)} itemPath={fullPageHref ?? undefined} />
        ))}
        {attachments.length === 0 && !uploading ? <div className="text-[12px] text-fg-faint">No attachments.</div> : null}
      </div>
    </section>
  ) : (
    <p className="mt-2 text-[12px] text-fg-faint">Attachments, comments and activity become available after you create the item.</p>
  );

  // JIRA-style typed, directional, cross-board links (blocks / depends on / relates to / …).
  // onChanged refreshes the drawer's own links AND the board behind it (so the card's link badge /
  // link_count update in THIS tab — the own-tab realtime echo is skipped by the board's live sync).
  const linksBlock = id ? <ItemLinksSection itemId={id} boardId={boardId} links={links} onChanged={() => { void loadSub(id); onSaved(); }} /> : null;
  // Wiki pages derived from the description (refetched when a save lands, so a newly-added wiki link shows).
  const wikiPagesBlock = id ? <ItemWikiPagesSection itemId={id} refreshKey={saveState === 'saved'} /> : null;

  // Direct sub-tasks + completion (a sub-task counts as finished when its status reads as a done or
  // dropped stage — the same keyword heuristic the status glyph uses). Lets the detail view double as a
  // little checklist of the work under this item.
  // For an EPIC this section lists the TOP-LEVEL items linked to it (epic_id === this epic AND no
  // parent — a sub-task inherits its parent's epic_id, so it's excluded here and shown under its parent).
  // For anything else it's the direct sub-tasks (parent_id). Both order by `position` and share the
  // same add + drag-reorder.
  const isEpic = meta?.type === 'epic';
  const subtasks = useMemo(
    () => (id ? items.filter((i) => (isEpic ? i.epic_id === id && !i.parent_id : i.parent_id === id)).sort((a, b) => a.position - b.position) : []),
    [items, id, isEpic],
  );
  // parent_id → its child items (sorted by position), across the whole board — lets the sub-tasks
  // section render the FULL descendant tree, not just this item's direct children (a sub-task can have
  // sub-tasks of its own).
  const childrenByParent = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const it of items) {
      if (!it.parent_id) continue;
      const arr = m.get(it.parent_id);
      if (arr) arr.push(it);
      else m.set(it.parent_id, [it]);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.position - b.position);
    return m;
  }, [items]);
  const [subClosed, subDropped] = useMemo(() => {
    const kinds = subtasks.map((s) => statusKind(statusMetaOf(s.status).label));
    return [kinds.filter(isClosedKind).length, kinds.filter((k) => k === 'dropped').length];
  }, [subtasks, statusMetaOf]);
  const subPct = subtasks.length ? Math.round((subClosed / subtasks.length) * 100) : 0;
  const complete = subtasks.length > 0 && subClosed === subtasks.length;
  const boardSlug = boards.find((b) => b.id === boardId)?.slug ?? '';
  // Opening a sub-task: on the full-screen page it NAVIGATES to that task's page; in a docked/modal
  // drawer it PUSHES a stacked drawer so Back returns here.
  const openSub = (sub: Item) => (isPage ? navigate(taskPath(boardSlug, sub.key)) : pushItem(sub.id));
  // Persist an inline field edit on a sub-task (a full item update — the backend preserves
  // color/epic_rank), then reload so the board + this list reflect it.
  const saveSub = useCallback(
    async (sub: Item, patch: Partial<Item>) => {
      const m = { ...sub, ...patch };
      try {
        await api.updateItem(sub.id, {
          board_id: m.board_id,
          parent_id: m.parent_id,
          epic_id: m.epic_id,
          title: m.title,
          description: m.description,
          status: m.status,
          priority: m.priority,
          type: m.type,
          assignee_id: m.assignee_id,
          pdm_id: m.pdm_id,
          source: m.source,
          start_date: m.start_date,
          end_date: m.end_date,
        });
      } finally {
        onSaved();
      }
    },
    [onSaved],
  );
  // ---- create-sub-task modal + drag-to-reorder ----
  const [newSubOpen, setNewSubOpen] = useState(false);
  // When set, the create modal targets THIS item as the parent (the "+" on a tree row); null = the
  // section-header "+", which creates under the drawer's own item.
  const [createParent, setCreateParent] = useState<Item | null>(null);
  // The drawer does NOT own `items` (a prop from the board/task page), so a reorder can't setItems.
  // Hold an optimistic reordered copy so the row jumps immediately, and drop it once the parent's
  // reload delivers the same order (matched by id) — no snap-back during the async round-trip.
  const [localSubs, setLocalSubs] = useState<Item[] | null>(null);
  // Which nodes are collapsed in the tree (default: everything expanded, so the nesting is visible).
  const [collapsedSubs, setCollapsedSubs] = useState<Set<string>>(() => new Set());
  const toggleCollapse = useCallback((sid: string) => {
    setCollapsedSubs((prev) => {
      const n = new Set(prev);
      if (n.has(sid)) n.delete(sid);
      else n.add(sid);
      return n;
    });
  }, []);
  // Drop the optimistic order once the server reload delivers the SAME order (the reorder landed), or
  // the moment the SET of sub-tasks changes (a concurrent add/delete makes the held order stale/wrong).
  useEffect(() => {
    if (!localSubs) return;
    const localIds = localSubs.map((s) => s.id);
    const serverIds = subtasks.map((s) => s.id);
    const orderLanded = localIds.join(',') === serverIds.join(',');
    const sameSet = localIds.length === serverIds.length && localIds.every((x) => serverIds.includes(x));
    if (orderLanded || !sameSet) setLocalSubs(null);
  }, [subtasks, localSubs]);
  const orderedSubs = localSubs ?? subtasks;
  const subSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onSubDragEnd = (e: DragEndEvent) => {
    if (!id) return;
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || activeId === overId) return;
    const from = orderedSubs.findIndex((s) => s.id === activeId);
    const to = orderedSubs.findIndex((s) => s.id === overId);
    if (from === -1 || to === -1) return;
    const next = arrayMove(orderedSubs, from, to);
    const before = next[to - 1];
    const after = next[to + 1];
    // Fractional midpoint — writes only the moved row, siblings keep their positions.
    let position: number;
    if (!before && !after) position = 1000;
    else if (!before) position = after.position - 1;
    else if (!after) position = before.position + 1;
    else position = (before.position + after.position) / 2;
    if (position === 0) position = -1; // 0 is the server's "keep old position" sentinel — never send it
    setLocalSubs(next.map((s) => (s.id === activeId ? { ...s, position } : s)));
    // Preserve the moved row's OWN parent (for sub-tasks it's this item; for epic-linked items it's
    // null/its real parent) — passing the epic id here would wrongly re-parent a linked item.
    const movedParent = orderedSubs[from]?.parent_id ?? null;
    api
      .reparentItem(activeId, movedParent, position) // pure position change (parent unchanged)
      .catch(() => setLocalSubs(null)) // on failure fall back to server truth
      .finally(() => onSaved());
  };
  // Create a child, appended to the end (fresh server-stamped position), then refresh so it appears.
  // On an EPIC it becomes a LINKED item (epic_id = this epic); elsewhere a sub-task (parent_id = this
  // item, inheriting the parent's epic).
  const createSubtask = useCallback(
    async (fields: { title: string; status: string; priority: Priority; assignee_id: string | null; type: TaskType }) => {
      if (!id) return;
      // Row "+" (createParent): a parent_id child of THAT row, inheriting its epic. Header "+" (null):
      // an epic-linked item on an epic, else a direct sub-task of the drawer item.
      const link = createParent
        ? { parent_id: createParent.id, epic_id: createParent.epic_id ?? null }
        : isEpic
          ? { epic_id: id }
          : { parent_id: id, epic_id: meta?.epic_id ?? null };
      const created = await api.createItem({
        board_id: boardId,
        ...link,
        title: fields.title.trim(),
        status: fields.status,
        priority: fields.priority,
        type: fields.type,
        assignee_id: fields.assignee_id,
      });
      record(existenceCommand(created.id, [created.id], created.board_id, `New ${created.key}`));
      onSaved();
    },
    [id, boardId, isEpic, meta, createParent, onSaved, record],
  );
  // Open the create modal, optionally under a specific tree row; creating under a row also expands it
  // so the new child is visible once the refresh lands.
  const openCreateUnder = useCallback((parent: Item | null) => {
    setCreateParent(parent);
    setNewSubOpen(true);
    if (parent)
      setCollapsedSubs((prev) => {
        const n = new Set(prev);
        n.delete(parent.id);
        return n;
      });
  }, []);

  const subtasksBlock = id ? (
    <>
      <section className={cn('border-t border-line pt-4', isPage ? 'mt-6' : 'mt-5')}>
        <div className="mb-3 flex items-center gap-2">
          <SubItem className="text-fg-faint" />
          <Label>{isEpic ? 'Top Level Linked Items' : 'Sub-tasks'}</Label>
          <span className="rounded-full bg-layer px-1.5 py-0.5 text-[11px] font-semibold tnum text-fg-faint">
            {subtasks.length}
          </span>
          <div className="ml-auto flex items-center gap-2.5">
            {subtasks.length > 0 ? (
              <>
                <div className="h-1.5 w-24 overflow-hidden rounded-full bg-layer">
                  <div
                    className={cn('h-full rounded-full transition-[width] duration-500 ease-out', complete ? 'bg-emerald-500' : 'bg-accent')}
                    style={{ width: `${subPct}%` }}
                  />
                </div>
                <span className={cn('tnum text-[12px] font-semibold', complete ? 'text-emerald-500' : 'text-fg-muted')} title={closedLabel(subClosed, subDropped, subtasks.length)}>
                  {subClosed}/{subtasks.length}
                </span>
              </>
            ) : null}
            <button
              type="button"
              onClick={() => openCreateUnder(null)}
              aria-label={isEpic ? 'Add linked item' : 'Add sub-task'}
              title={isEpic ? 'Add linked item' : 'Add sub-task'}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
        {subtasks.length > 0 ? (
          <DndContext sensors={subSensors} collisionDetection={closestCenter} onDragEnd={onSubDragEnd}>
            <SortableContext items={orderedSubs.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              <ul className="overflow-hidden rounded-xl border border-line">
                {orderedSubs.map((s, i) => (
                  <SortableSubtaskRow
                    key={s.id}
                    item={s}
                    depth={0}
                    topBorder={i > 0}
                    childrenByParent={childrenByParent}
                    collapsed={collapsedSubs}
                    onToggleCollapse={toggleCollapse}
                    statuses={statuses}
                    members={activeMembers}
                    onSave={saveSub}
                    onOpen={openSub}
                    onCreateChild={openCreateUnder}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        ) : (
          <p className="rounded-xl border border-dashed border-line px-3 py-6 text-center text-[12px] text-fg-faint">
            {isEpic ? 'No top-level linked items yet' : 'No sub-tasks yet'} — add one with the + above.
          </p>
        )}
      </section>
      <NewSubtaskModal
        open={newSubOpen}
        onClose={() => {
          setNewSubOpen(false);
          setCreateParent(null);
        }}
        onCreate={createSubtask}
        noun={createParent || !isEpic ? 'sub-task' : 'linked item'}
        statuses={statuses}
        members={activeMembers}
        defaultStatus={defaultStatusRef.current}
      />
    </>
  ) : null;

  // The drawer's inner content, shared verbatim by the modal and the docked presentations.
  const body = (
    <>
      {/* Drop veil — the panel is a transformed ancestor, so this `fixed` layer is sized and
         clipped to the panel itself. Purely visual and non-interactive (the drop is handled by the
         window listener above); `pointer-events-none` keeps it from ever swallowing the drop. */}
      {dragging ? (
        <div className="pointer-events-none fixed inset-0 z-30 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-surface-raised/85 backdrop-blur-[2px]" />
          <div className="relative flex flex-col items-center gap-4 rounded-2xl border-2 border-dashed border-accent bg-surface-inset px-14 py-10 text-center shadow-lg">
            {/* A little stack of file sheets with one settling onto it on a loop (see .drop-art in
                index.scss) — reads as "let go and it lands here". */}
            <div className="drop-art" aria-hidden="true">
              <span className="drop-sheet drop-sheet--left" />
              <span className="drop-sheet drop-sheet--right" />
              <span className="drop-sheet drop-sheet--front" />
            </div>
            <div>
              <div className="text-[14px] font-semibold text-fg-strong">Drop to attach</div>
              <div className="mt-0.5 text-[12px] text-fg-faint">Images or files — release anywhere</div>
            </div>
          </div>
        </div>
      ) : null}
      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner className="h-6 w-6" />
        </div>
      ) : deletedExternally ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="text-[14px] font-semibold text-fg-strong">This item was deleted</div>
          <p className="mt-1.5 max-w-[280px] text-[12px] leading-relaxed text-fg-faint">
            Someone removed this item while you had it open. Close this panel to go back.
          </p>
          <Button variant="outline" className="mt-4" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="text-[14px] font-semibold text-fg-strong">Couldn’t load this item</div>
          <p className="mt-1.5 max-w-[260px] text-[12px] leading-relaxed text-fg-faint">
            It may have been deleted, or the connection dropped. Close this panel and try again.
          </p>
        </div>
      ) : (
        // Keyed by `contentKey` so switching cards while docked plays a gentle enter animation on
        // the new item's content (see `panel-swap` in tailwind.config) — an organic swap rather
        // than an instant repaint. The counter is bumped only on a genuine open/switch, NOT when an
        // autosave create promotes the draft in place (which flips both the internal id and the
        // itemId prop from null→created.id): keying on either of those would remount the subtree
        // mid-edit and drop focus. Stable across tab/field changes too.
        <div key={contentKey} className="animate-panel-swap">
          {/* Tabs — a sticky strip that pins under the modal header while a tab's content scrolls.
             The scroll body has 20px (`py-5`) of top padding, which sticky treats as the top
             inset: at rest `-mt-5` cancels it so the strip sits flush below the header, and while
             stuck `-top-5` cancels it again so the strip pins flush there too — without the latter
             the strip would stick 20px lower, leaving a band the scrolling content shows through.
             `mb-8` then holds the panel content clear of the tab divider so it never crowds it. */}
          <div
            role="tablist"
            aria-label="Item sections"
            className={cn(
              'z-10 flex gap-5 border-b border-line',
              // The drawer/modal pins the tab strip under its header (negative-margin trick). The
              // full page has no header inside the card, so it just sits with normal spacing.
              isPage ? 'mb-7 pt-0' : 'sticky -top-5 -mt-5 mb-8 bg-surface-raised pt-3',
            )}
          >
            {/* On the very wide page the Comments tab is dropped — comments live in the right rail. */}
            {(wideComments ? TABS.filter((t) => t.key !== 'comments') : TABS).map((t) => (
              <DrawerTab
                key={t.key}
                active={effectiveTab === t.key}
                disabled={t.disabled}
                count={t.count}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </DrawerTab>
            ))}
          </div>

          {/* Upload status — kept at the modal level (not inside the Details panel) so a paste or
             drop is acknowledged on any tab, and announced to assistive tech. A drop/paste on the
             Comments or Activity tab would otherwise upload with nothing on screen to show for it. */}
          {uploading || uploadError ? (
            <div className="-mt-4 mb-6 space-y-2">
              {uploading ? (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex items-center gap-2 rounded-lg bg-surface-inset px-3 py-1.5 text-[12px] text-fg-faint"
                >
                  <Spinner className="h-3.5 w-3.5" /> Uploading…
                </div>
              ) : null}
              {uploadError ? (
                <div
                  role="alert"
                  className="flex items-start justify-between gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-1.5 text-[12px] text-danger"
                >
                  <span>{uploadError}</span>
                  <button
                    type="button"
                    onClick={() => setUploadError(null)}
                    aria-label="Dismiss upload error"
                    className="shrink-0 text-danger/70 hover:text-danger"
                  >
                    ✕
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Details — every field from title through attachments. */}
          {effectiveTab === 'details' ? (
            <div role="tabpanel" aria-label="Details">
              {/* Title as the record's heading — quiet at rest, becomes a field on focus — rather
                  than another boxed input. Don't grab focus in the dock (browsing cards on the board
                  shouldn't yank the cursor into the title each time); a modal still autofocuses. */}
              <input
                className={cn(
                  'w-full rounded-lg bg-transparent px-1.5 py-2 font-semibold leading-snug text-fg-strong placeholder:text-fg-ghost transition-colors hover:bg-surface-inset focus:bg-surface-inset focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent/40',
                  isPage ? 'mb-6 text-[22px]' : 'mb-4 text-[17px]',
                )}
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Untitled"
                aria-label="Title"
                data-hl="field:title"
                autoFocus={!docked}
              />

              {isPage ? (
                // Full page: description + attachments in the wide left column; properties in a rail.
                // On a very wide screen a THIRD column holds comments, always in view (`wideComments`)
                // — the Comments tab is hidden there, so nothing is duplicated.
                <div className={cn('grid grid-cols-1 gap-x-8', wideComments ? 'grid-cols-[minmax(0,1fr)_320px_minmax(340px,400px)]' : 'lg:grid-cols-[minmax(0,1fr)_320px]')}>
                  <div className="min-w-0">
                    {descBlock}
                    {subtasksBlock}
                    {attachBlock}
                    {linksBlock}
                    {wikiPagesBlock}
                  </div>
                  <div className="min-w-0">{propsTable}</div>
                  {wideComments ? (
                    <div className="min-w-0">
                      <div className="mb-3 flex items-baseline gap-2 border-b border-line pb-2 text-[13px] font-semibold text-fg-strong">
                        Comments{id && comments.length ? <span className="text-fg-faint">· {comments.length}</span> : null}
                      </div>
                      {commentsBlock}
                    </div>
                  ) : null}
                </div>
              ) : (
                <>
                  {propsSection}
                  {descBlock}
                  {subtasksBlock}
                  {attachBlock}
                  {linksBlock}
                  {wikiPagesBlock}
                </>
              )}
            </div>
          ) : null}

          {/* Comments — its own tab (narrow); on a wide page it moves to the right rail (below). */}
          {effectiveTab === 'comments' ? <div role="tabpanel" aria-label="Comments">{commentsBlock}</div> : null}

          {/* Activity — its own tab, including the created/updated summary. */}
          {effectiveTab === 'activity' ? (
            <div role="tabpanel" aria-label="Activity">
              {meta ? (
                <div className="mb-4 rounded-lg bg-surface-inset px-3 py-2 text-[11px] leading-relaxed text-fg-faint">
                  Created {fmtDate(meta.created_at)}
                  {meta.created_by ? ` by ${memberName(meta.created_by) ?? 'someone'}` : ''}
                  {' · '}Updated {timeAgo(meta.updated_at)}
                  {meta.updated_by ? ` by ${memberName(meta.updated_by) ?? 'someone'}` : ''}
                </div>
              ) : null}
              <ul className="space-y-3">
                {activity.map((a) => {
                  const author = members.find((m) => m.id === a.member_id);
                  const changes = a.kind === 'updated' ? itemChanges(a.old, a.new, memberName, items, statusMetaOf) : [];
                  return (
                    <li key={a.id} className="flex items-start gap-2.5">
                      <MemberAvatar member={author} size={22} className="mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] text-fg-muted">
                          <span className="font-semibold text-fg-soft">{memberName(a.member_id) ?? 'Someone'}</span>{' '}
                          {describeAction(a, changes, statusMetaOf)}
                          <span className="ml-1 text-fg-ghost" title={fmtDateTime(a.created_at)}>
                            · {timeAgo(a.created_at)}
                          </span>
                        </div>
                        {changes.length ? (
                          <ul className="mt-1 space-y-0.5">
                            {changes.map((c) => (
                              <li key={c.label} className="text-[11px] text-fg-faint">
                                <span className="text-fg-soft">{c.label}</span>
                                {c.from || c.to ? (
                                  <>
                                    {': '}
                                    <span className="text-fg-ghost line-through">{c.from || '—'}</span>
                                    {' → '}
                                    <span className="text-fg-muted">{c.to || '—'}</span>
                                  </>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
                {activity.length === 0 ? <li className="text-[12px] text-fg-faint">No activity yet.</li> : null}
              </ul>
            </div>
          ) : null}

          {/* Stats — time spent in each status + time to complete, reconstructed from the activity trail. */}
          {effectiveTab === 'stats' ? (
            <div role="tabpanel" aria-label="Stats">
              {statsTimeline && statsTimeline.perStatus.length ? (
                (() => {
                  // A closed status (completed, or dropped: Won't fix, Archived…) is post-work idle time,
                  // not cycle time: its dwell just grows forever and dwarfs the real signals — so we drop
                  // the "in <closed status>" headline card and leave closed statuses out of the breakdown.
                  const currentClosed = isClosedKind(statusKind(statusMetaOf(statsTimeline.currentValue).label));
                  const bars = statsTimeline.perStatus.filter((s) => !isClosedKind(statusKind(statusMetaOf(s.value).label)));
                  // Nothing measurable — e.g. a completed seed row with no status history at all (no
                  // bars, and no computable time-to-complete): say so rather than render an empty shell.
                  if (!bars.length && statsTimeline.timeToCompleteDays == null) {
                    return <div className="text-[12px] text-fg-faint">Not enough status history yet.</div>;
                  }
                  const max = Math.max(...bars.map((s) => s.days), 0.0001);
                  return (
                    <div className="space-y-5">
                      <div className="flex gap-3">
                        <div className="flex-1 rounded-xl border border-line px-3.5 py-3">
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">Time to complete</div>
                          <div className="mt-1 tnum text-[20px] font-semibold text-fg-strong">{statsTimeline.timeToCompleteDays != null ? formatDays(statsTimeline.timeToCompleteDays) : '—'}</div>
                          <div className="mt-0.5 text-[11px] text-fg-faint">{statsTimeline.timeToCompleteFrom === 'created' ? 'Created → Complete' : 'In Progress → Complete'}</div>
                        </div>
                        {!currentClosed ? (
                          <div className="flex-1 rounded-xl border border-line px-3.5 py-3">
                            <div className="truncate text-[11px] font-semibold uppercase tracking-wide text-fg-faint">In {statusMetaOf(statsTimeline.currentValue).label}</div>
                            <div className="mt-1 tnum text-[20px] font-semibold text-fg-strong">{formatDays(statsTimeline.currentDays)}</div>
                            <div className="mt-0.5 text-[11px] text-fg-faint">current status · {formatDays(statsTimeline.ageDays)} old</div>
                          </div>
                        ) : null}
                      </div>
                      {bars.length ? (
                        <div>
                          <Label>Time in each status</Label>
                          <div className="mt-2 space-y-2">
                            {bars.map((seg) => (
                              <div key={seg.value} className="flex items-center gap-2.5 text-[12.5px]">
                                <StatusDot value={seg.value} size={16} />
                                <span className="w-28 shrink-0 truncate text-fg-soft">{statusMetaOf(seg.value).label}</span>
                                <div className="h-2 flex-1 overflow-hidden rounded-full bg-layer">
                                  <div className="h-full rounded-full bg-accent/70" style={{ width: `${Math.max(3, (seg.days / max) * 100)}%` }} />
                                </div>
                                <span className="w-12 shrink-0 text-right tnum text-fg-faint">{formatDays(seg.days)}</span>
                              </div>
                            ))}
                          </div>
                          {/* Summary line beneath the bars: the item's time-to-complete, set off by a
                              rule. It has no bar of its own and is not part of `bars`, so it can't affect
                              the scale of the per-status bars above it. */}
                          <div className="mt-2.5 flex items-center gap-2.5 border-t border-line pt-2.5 text-[12.5px]">
                            <span className="flex-1 text-fg-soft">Time to complete</span>
                            <span className="shrink-0 tnum font-semibold text-fg-strong">{statsTimeline.timeToCompleteDays != null ? formatDays(statsTimeline.timeToCompleteDays) : '—'}</span>
                          </div>
                        </div>
                      ) : null}
                      <p className="text-[11px] leading-relaxed text-fg-faint">
                        Reconstructed from this item’s status history — each status’s total sums <span className="text-fg-soft">every</span> visit to it across the item’s whole lifecycle. Time in a completed or dropped status (Dropped, Won’t fix, Archived…) is left out (it isn’t cycle time and only grows once the item is closed). “Time to complete” runs from when it first entered an{' '}
                        <span className="text-fg-soft">in-progress</span> status to when it <span className="text-fg-soft">last</span> reached a <span className="text-fg-soft">complete</span> one; if it was finished without ever being in-progress, it’s measured from when the item was <span className="text-fg-soft">created</span> instead. It stays “—” until the item is complete, so a dropped item never gets one. All durations are <span className="text-fg-soft">calendar time</span> — weekends and non-working hours included.
                      </p>
                    </div>
                  );
                })()
              ) : (
                <div className="text-[12px] text-fg-faint">Not enough status history yet.</div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </>
  );

  // A pinned dock is a persistent sidebar: with nothing open it shows a guiding empty state
  // instead of vanishing. `open` is true for both editing an item and the create form.
  const hasContent = open;
  const emptyTitle = <div className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">Details</div>;
  // Hide the empty dock so the board reclaims the 440px; it slides back the moment a card is opened.
  const hideBtn = (
    <button
      type="button"
      onClick={hideEmpty}
      aria-label="Hide panel"
      title="Hide panel"
      className="-mr-1 flex h-9 w-9 items-center justify-center rounded-full text-fg-faint transition-colors hover:bg-layer-strong hover:text-fg"
    >
      <HidePanel className="h-4 w-4" />
    </button>
  );
  const emptyState = (
    <div className="animate-panel-swap flex min-h-full flex-col items-center justify-center py-10 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-inset text-fg-faint">
        <OpenPanel className="h-6 w-6" />
      </div>
      <div className="text-[14px] font-semibold text-fg-strong">No task selected</div>
      <p className="mt-1.5 max-w-[240px] text-[12px] leading-relaxed text-fg-faint">
        Pick a task on the board to see its details here — the panel stays pinned while you work.
      </p>
    </div>
  );

  // Full-screen page presentation (the /:slug/:key route): render the SAME body verbatim inside a
  // plain full-width card — no modal/dock chrome, no pin toggle — with the title + actions as a
  // header. Reuses every tab (details / comments / activity / attachments) and the autosave.
  if (presentation === 'page') {
    return (
      <>
        {/* Width is owned by the page (TaskPage) so the breadcrumb + this card share one column.
            The editable title lives inside the card (and in the breadcrumb) — no redundant header;
            just a slim actions row (save status + ⋮). */}
        <div className="w-full">
          {footer ? <div className="mb-3 flex min-h-[36px] items-center justify-end gap-2">{footer}</div> : null}
          <div className="overflow-hidden rounded-2xl border border-line bg-surface-raised px-5 py-6 sm:px-8 sm:py-7">
            {body}
          </div>
        </div>
        <ConfirmDialog
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          onConfirm={doDelete}
          title="Delete item?"
          message="This item and its comments, attachments and history will be permanently removed. This can’t be undone."
          confirmLabel="Delete"
          busy={deleting}
        />
      </>
    );
  }

  return (
    <>
      {/* Both surfaces stay mounted so their open/close SLIDES animate; only one is ever visible
          at a time (the other renders null). The dock is present whenever pinned (docked) — showing
          the empty state when no item is open — while the modal shows only when unpinned and open. */}
      <Modal open={!docked && open} onClose={onClose} title={title} headerAccessory={copyLinkBtn} footer={footer} className="max-w-[720px] sm:h-[90dvh]">
        {body}
      </Modal>
      <DrawerDock
        open={dockVisible}
        onClose={onClose}
        showClose={hasContent}
        title={hasContent ? title : emptyTitle}
        headerAccessory={hasContent ? copyLinkBtn : hideBtn}
        footer={hasContent ? footer : undefined}
        ariaLabel="Item details"
        width={dockWidth}
        onResize={setDockWidth}
      >
        {hasContent ? body : emptyState}
      </DrawerDock>
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={doDelete}
        title="Delete item?"
        message="This item and its comments, attachments and history will be permanently removed. This can’t be undone."
        confirmLabel="Delete"
        busy={deleting}
      />
    </>
  );
}

function DrawerTab({
  active,
  disabled,
  count,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  count?: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      disabled={disabled}
      onClick={onClick}
      title={disabled ? 'Available after you create the item' : undefined}
      className={cn(
        'relative -mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-0.5 pb-2.5 pt-1 text-[13px] font-semibold transition-colors',
        disabled
          ? 'cursor-not-allowed border-transparent text-fg-ghost'
          : active
            ? 'border-accent text-fg-strong'
            : 'border-transparent text-fg-faint hover:text-fg-soft',
      )}
    >
      {children}
      {count ? (
        <span
          className={cn(
            'tnum rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none',
            active ? 'bg-accent/15 text-accent' : 'bg-layer text-fg-faint',
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

/**
 * One row in the detail view's sub-task checklist. The key + title open the sub-task (stacked
 * drawer, or a navigation on the full page); the trailing cluster — priority · status · assignee,
 * in that order — is inline-editable via the same pickers the board uses. Field edits apply
 * optimistically (a local overlay) and persist through `onSave`, so the row updates instantly.
 */
// Maximum tree depth we recurse to render — a backstop against pathological / cyclic parent_id data.
const MAX_SUBTASK_DEPTH = 20;

// SubtaskRowBody is the presentational sub-task row: an optional lead (drag handle for top-level rows,
// a spacer for nested ones), depth indentation, an expand/collapse chevron when the row has children,
// then the key + title (opens the item) and the inline priority / status / assignee pickers.
function SubtaskRowBody({
  item,
  statuses,
  members,
  onSave,
  onOpen,
  onCreateChild,
  depth,
  lead,
  expand,
  topBorder,
}: {
  item: Item;
  statuses: Status[];
  members: Member[];
  onSave: (item: Item, patch: Partial<Item>) => void;
  onOpen: () => void;
  onCreateChild: () => void;
  depth: number;
  lead: ReactNode;
  expand: { open: boolean; onToggle: () => void } | null;
  topBorder: boolean;
}) {
  const metaOf = useStatusMeta();
  const memberCreate = useMemberCreate();
  const [ov, setOv] = useState<Partial<Item>>({});
  const s = { ...item, ...ov };
  const save = (patch: Partial<Item>) => {
    setOv((o) => ({ ...o, ...patch }));
    // Carry any earlier in-flight edit on this row (the overlay `ov`) into the write, so changing a
    // second field before the first save's refetch lands doesn't revert the first to its old value.
    onSave({ ...item, ...ov, ...patch }, patch);
  };
  const closed = isClosedKind(statusKind(metaOf(s.status).label));
  const pm = priorityMeta(s.priority);
  const sm = metaOf(s.status);
  const member = members.find((m) => m.id === s.assignee_id);

  const statusOpts: SelectOption[] = statuses.map((st) => ({ value: st.value, label: st.label, leading: <StatusDot value={st.value} size={16} /> }));
  const prioOpts: SelectOption[] = PRIORITIES.map((p) => ({
    value: p.value,
    label: p.label,
    leading: <PriorityChevron level={p.level} color={p.color} />,
  }));
  const memberOpts: SelectOption[] = [
    { value: '', label: 'Unassigned', leading: <MemberAvatar member={undefined} size={18} />, tone: 'text-fg-muted' },
    ...members.map((m) => ({ value: m.id, label: m.name, leading: <MemberAvatar member={m} size={18} />, keywords: m.email ?? '' })),
  ];
  const pickBtn = 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-layer-strong';

  return (
    <div className={cn('group/row flex w-full items-center gap-1 bg-surface-raised px-2 py-2 transition-colors hover:bg-layer', topBorder && 'border-t border-line')}>
      {/* Drag handle (top level) or an equal-width spacer (nested), so key chips line up down the tree. */}
      {lead}
      {depth > 0 ? <span className="shrink-0" style={{ width: depth * 18 }} aria-hidden /> : null}
      {expand ? (
        <button
          type="button"
          onClick={expand.onToggle}
          className="flex h-6 w-5 shrink-0 items-center justify-center rounded text-fg-ghost transition-colors hover:text-fg-soft"
          aria-label={expand.open ? 'Collapse sub-tasks' : 'Expand sub-tasks'}
          aria-expanded={expand.open}
          title={expand.open ? 'Collapse' : 'Expand'}
        >
          <Chevron className={cn('h-3.5 w-3.5 transition-transform', expand.open && 'rotate-90')} />
        </button>
      ) : (
        <span className="h-6 w-5 shrink-0" aria-hidden />
      )}
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-2 px-1 text-left">
        {/* Task type glyph in front of the key, so the tree reads bug / story / task at a glance. */}
        <TaskTypeGlyph type={item.type} color={taskTypeMeta(item.type).color} size={16} className="shrink-0" />
        <span className="item-key shrink-0 rounded-md px-1.5 py-0.5 tnum text-[11px] font-semibold">{item.key}</span>
        <span className={cn('min-w-0 flex-1 truncate text-[13px]', closed ? 'text-fg-faint line-through decoration-fg-ghost' : 'text-fg')}>
          {s.title || 'Untitled'}
        </span>
        <OpenPanel className="h-3.5 w-3.5 shrink-0 text-fg-ghost opacity-0 transition-opacity group-hover/row:opacity-100" />
      </button>
      {/* Add a sub-task UNDER this row — revealed on hover, next to the open-in-drawer icon. */}
      <button
        type="button"
        onClick={onCreateChild}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-fg-ghost opacity-0 transition-all hover:bg-layer-strong hover:text-fg group-hover/row:opacity-100"
        title="Add sub-task"
        aria-label="Add sub-task under this item"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
      {/* priority · status · assignee — all inline-editable */}
      <SelectPopover
        options={prioOpts}
        value={s.priority}
        onChange={(v) => save({ priority: v as Priority })}
        searchPlaceholder="Search priority…"
        trigger={({ open, toggle }) => (
          <button type="button" onClick={toggle} aria-haspopup="listbox" aria-expanded={open} className={pickBtn} title={`Priority: ${pm.label}`}>
            <PriorityChevron level={pm.level} color={pm.color} />
          </button>
        )}
      />
      <SelectPopover
        options={statusOpts}
        value={s.status}
        onChange={(v) => save({ status: v })}
        searchPlaceholder="Search statuses…"
        trigger={({ open, toggle }) => (
          <button type="button" onClick={toggle} aria-haspopup="listbox" aria-expanded={open} className={pickBtn} title={`Status: ${sm.label}`}>
            <StatusDot value={s.status} size={18} />
          </button>
        )}
      />
      <SelectPopover
        options={memberOpts}
        value={s.assignee_id ?? ''}
        onChange={(v) => save({ assignee_id: v || null })}
        {...memberCreate((id) => save({ assignee_id: id }))}
        searchPlaceholder="Search assignee…"
        trigger={({ open, toggle }) => (
          <button type="button" onClick={toggle} aria-haspopup="listbox" aria-expanded={open} className={cn(pickBtn, 'hover:scale-105')} title={`Assignee: ${member?.name ?? 'Unassigned'}`}>
            <MemberAvatar member={member} size={20} />
          </button>
        )}
      />
    </div>
  );
}

// Shared props for a node in the sub-task tree.
type SubtaskTreeProps = {
  item: Item;
  depth: number;
  childrenByParent: Map<string, Item[]>;
  collapsed: Set<string>;
  onToggleCollapse: (id: string) => void;
  statuses: Status[];
  members: Member[];
  onSave: (item: Item, patch: Partial<Item>) => void;
  onOpen: (item: Item) => void;
  onCreateChild: (parent: Item) => void;
};

// SortableSubtaskRow is a TOP-LEVEL sub-task (depth 0): draggable to reorder among its siblings, with
// its whole descendant subtree nested INSIDE its <li> so the subtree travels with the drag. Listeners
// live only on the grip so dragging never fires onOpen or the inline pickers.
function SortableSubtaskRow({ topBorder, ...p }: SubtaskTreeProps & { topBorder: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.item.id });
  const kids = p.childrenByParent.get(p.item.id) ?? [];
  const open = !p.collapsed.has(p.item.id);
  const grip = (
    <span
      {...attributes}
      {...listeners}
      className="flex h-7 w-4 shrink-0 cursor-grab touch-none items-center justify-center rounded text-fg-ghost transition-colors hover:text-fg-soft active:cursor-grabbing"
      title="Drag to reorder"
      aria-label="Drag to reorder sub-task"
    >
      <Grip className="h-4 w-4" />
    </span>
  );
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }} className={cn(isDragging && 'relative z-10 opacity-70')}>
      <SubtaskRowBody
        item={p.item}
        statuses={p.statuses}
        members={p.members}
        onSave={p.onSave}
        onOpen={() => p.onOpen(p.item)}
        onCreateChild={() => p.onCreateChild(p.item)}
        depth={0}
        lead={grip}
        topBorder={topBorder}
        expand={kids.length ? { open, onToggle: () => p.onToggleCollapse(p.item.id) } : null}
      />
      {open && kids.length > 0 ? (
        <ul>
          {kids.map((c) => (
            <SubtaskNode key={c.id} {...p} item={c} depth={1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

// SubtaskNode is a nested (depth ≥ 1) sub-task: shown with indentation + expand/collapse, inline-
// editable and openable, but not drag-reorderable here — reorder a deeper level by opening its parent
// (each item's own detail view lists ITS children with the same drag affordance).
function SubtaskNode(p: SubtaskTreeProps) {
  const kids = p.childrenByParent.get(p.item.id) ?? [];
  const open = !p.collapsed.has(p.item.id);
  return (
    <li>
      <SubtaskRowBody
        item={p.item}
        statuses={p.statuses}
        members={p.members}
        onSave={p.onSave}
        onOpen={() => p.onOpen(p.item)}
        onCreateChild={() => p.onCreateChild(p.item)}
        depth={p.depth}
        lead={<span className="h-7 w-4 shrink-0" aria-hidden />}
        topBorder
        expand={kids.length ? { open, onToggle: () => p.onToggleCollapse(p.item.id) } : null}
      />
      {open && kids.length > 0 && p.depth < MAX_SUBTASK_DEPTH ? (
        <ul>
          {kids.map((c) => (
            <SubtaskNode key={c.id} {...p} item={c} depth={p.depth + 1} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** Lightweight "New sub-task" modal — title (required) + status / priority / assignee pickers. On submit
 *  it delegates to `onCreate` (which stamps parent_id + epic + refreshes), then closes. */
function NewSubtaskModal({
  open,
  onClose,
  onCreate,
  noun = 'sub-task',
  statuses,
  members,
  defaultStatus,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (f: { title: string; status: string; priority: Priority; assignee_id: string | null; type: TaskType }) => Promise<void>;
  noun?: string;
  statuses: Status[];
  members: Member[];
  defaultStatus: string;
}) {
  const metaOf = useStatusMeta();
  const memberCreate = useMemberCreate();
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState(defaultStatus);
  const [priority, setPriority] = useState<Priority>(DEFAULT_PRIORITY);
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset every time the modal (re)opens so a previous draft never lingers.
  useEffect(() => {
    if (open) {
      setTitle('');
      setStatus(defaultStatus);
      setPriority(DEFAULT_PRIORITY);
      setAssignee('');
      setBusy(false);
      setErr(null);
    }
  }, [open, defaultStatus]);

  const submit = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await onCreate({ title, status, priority, assignee_id: assignee || null, type: DEFAULT_TASK_TYPE });
      onClose();
    } catch {
      setErr('Couldn’t create the sub-task. Please try again.');
      setBusy(false);
    }
  };

  const statusOpts: SelectOption[] = statuses.map((st) => ({ value: st.value, label: st.label, leading: <StatusDot value={st.value} size={16} /> }));
  const prioOpts: SelectOption[] = PRIORITIES.map((p) => ({ value: p.value, label: p.label, leading: <PriorityChevron level={p.level} color={p.color} /> }));
  const memberOpts: SelectOption[] = [
    { value: '', label: 'Unassigned', leading: <MemberAvatar member={undefined} size={18} />, tone: 'text-fg-muted' },
    ...members.map((m) => ({ value: m.id, label: m.name, leading: <MemberAvatar member={m} size={18} />, keywords: m.email ?? '' })),
  ];
  const sm = metaOf(status);
  const pm = priorityMeta(priority);
  const member = members.find((m) => m.id === assignee);
  const pickBtn = 'flex h-9 items-center gap-2 rounded-lg border border-line px-2.5 text-[13px] text-fg transition-colors hover:bg-layer';

  return (
    <Modal open={open} onClose={onClose} title={`New ${noun}`} className="max-w-[440px]">
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-fg-muted">Title</span>
          <input className={inputCls} value={title} autoFocus onChange={(e) => setTitle(e.target.value)} placeholder={`${noun.charAt(0).toUpperCase()}${noun.slice(1)} title`} onKeyDown={(e) => e.key === 'Enter' && submit()} />
        </label>
        <div className="flex flex-wrap gap-2">
          <SelectPopover
            options={statusOpts}
            value={status}
            onChange={setStatus}
            searchPlaceholder="Search statuses…"
            trigger={({ open: o, toggle }) => (
              <button type="button" onClick={toggle} aria-haspopup="listbox" aria-expanded={o} className={pickBtn}>
                <StatusDot value={status} size={16} />
                <span className="text-fg-muted">{sm.label}</span>
              </button>
            )}
          />
          <SelectPopover
            options={prioOpts}
            value={priority}
            onChange={(v) => setPriority(v as Priority)}
            searchPlaceholder="Search priority…"
            trigger={({ open: o, toggle }) => (
              <button type="button" onClick={toggle} aria-haspopup="listbox" aria-expanded={o} className={pickBtn}>
                <PriorityChevron level={pm.level} color={pm.color} />
                <span className="text-fg-muted">{pm.label}</span>
              </button>
            )}
          />
          <SelectPopover
            options={memberOpts}
            value={assignee}
            onChange={(v) => setAssignee(v)}
            {...memberCreate((mid) => setAssignee(mid))}
            searchPlaceholder="Search assignee…"
            trigger={({ open: o, toggle }) => (
              <button type="button" onClick={toggle} aria-haspopup="listbox" aria-expanded={o} className={pickBtn}>
                <MemberAvatar member={member} size={18} />
                <span className="text-fg-muted">{member?.name ?? 'Unassigned'}</span>
              </button>
            )}
          />
        </div>
        {err ? <p className="text-[12px] text-danger">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy || !title.trim()}>
            {busy ? 'Creating…' : `Create ${noun}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * One attachment row. Its bytes are fetched THROUGH axios (so the Bearer token rides along — a
 * plain <img>/<a> to the API URL would 401 under OIDC) and wrapped in an object URL for the
 * thumbnail + open link. The object URL is revoked on unmount.
 */
function AttachmentItem({ att, attachments, onDelete, itemPath }: { att: Attachment; attachments: Attachment[]; onDelete: () => void; itemPath?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const openLightbox = useLightbox();
  const kind = attachmentKind(att);
  const isImage = kind === 'image';
  const isVideo = kind === 'video';
  const isHtml = kind === 'html';
  const isDoc = kind === 'pdf' || kind === 'docx' || kind === 'xlsx' || kind === 'pptx';
  const viewable = isPreviewable(att);
  useEffect(() => {
    // Fetch a preview blob only for images (the thumbnail). Videos/docs use an icon.
    if (!isImage) return undefined;
    let cancelled = false;
    let obj: string | null = null;
    setUrl(null);
    setFailed(false);
    api
      .fetchAttachmentBlob(att.id)
      .then((blob) => {
        if (cancelled) return;
        obj = URL.createObjectURL(blob);
        setUrl(obj);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [att.id, isImage]);

  const open = () => (viewable && openLightbox ? openLightbox(attachments, att.id, itemPath) : undefined);
  // The bytes are auth'd (a bare href 401s), so download fetches the blob first, then saves it.
  const download = () => {
    api
      .fetchAttachmentBlob(att.id)
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = att.filename || 'download';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(u), 60000);
      })
      .catch(() => undefined);
  };

  return (
    <div className="flex items-center gap-2.5 rounded-lg bg-surface-inset px-3 py-1.5 text-[13px]">
      {isImage && url ? (
        <button type="button" onClick={open} className="shrink-0" aria-label={`Open ${att.filename}`}>
          <img src={url} alt="" className="h-9 w-9 rounded object-cover ring-1 ring-line" />
        </button>
      ) : (
        <button type="button" onClick={open} disabled={!viewable} className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded bg-layer text-fg-faint', viewable && 'hover:text-fg')}>
          {isImage && !failed ? (
            <Spinner className="h-3.5 w-3.5" />
          ) : isVideo ? (
            <PlayIcon />
          ) : isHtml ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" /></svg>
          ) : isDoc ? (
            <span
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded text-[8px] font-bold text-white',
                kind === 'pdf' ? 'bg-[#e2483d]' : kind === 'docx' ? 'bg-[#2b579a]' : kind === 'xlsx' ? 'bg-[#217346]' : 'bg-[#c43e1c]',
              )}
            >
              {kind === 'pdf' ? 'PDF' : kind === 'docx' ? 'DOC' : kind === 'xlsx' ? 'XLS' : 'PPT'}
            </span>
          ) : (
            <FileIcon />
          )}
        </button>
      )}
      <button
        type="button"
        onClick={viewable ? open : undefined}
        disabled={!viewable}
        className={cn('min-w-0 flex-1 truncate text-left', viewable ? 'text-accent hover:underline' : 'text-fg-soft')}
        title={att.filename}
      >
        {att.filename}
      </button>
      <span className="tnum shrink-0 text-[11px] text-fg-faint">{(att.size / 1024).toFixed(1)} KB</span>
      <button
        type="button"
        onClick={download}
        className="flex h-10 w-9 shrink-0 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-layer-strong hover:text-fg"
        aria-label={`Download ${att.filename}`}
        title="Download"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" /></svg>
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="flex h-10 w-9 shrink-0 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-layer-strong hover:text-danger"
        aria-label={`Delete ${att.filename}`}
      >
        ✕
      </button>
    </div>
  );
}

/** Play triangle — the tile for a video attachment. */
function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

/** Generic document glyph — the stand-in tile for a non-image attachment. */
function FileIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

/** Small check glyph for the settled "Saved" state. */
function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

/** The autosave status line that stands in for the removed Save button: a required-title nudge,
 *  a live "Saving…", a settled "Saved", or a failure the user can retry. */
function SaveIndicator({ state, titleEmpty, onRetry }: { state: SaveState; titleEmpty: boolean; onRetry: () => void }) {
  // A missing title takes precedence — it's why nothing is being persisted (except once we've
  // already saved, where it can't happen: an item can't have been saved with an empty title).
  if (titleEmpty && state !== 'saved') {
    return <span className="text-[12px] text-fg-faint">Add a title to save</span>;
  }
  if (state === 'saving') {
    return (
      <span role="status" aria-live="polite" className="flex items-center gap-1.5 text-[12px] text-fg-faint">
        <Spinner className="h-3.5 w-3.5" /> Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span role="status" aria-live="polite" className="flex items-center gap-1.5 text-[12px] text-fg-faint">
        <span className="text-ok">
          <CheckIcon />
        </span>{' '}
        Saved
      </span>
    );
  }
  if (state === 'error') {
    return (
      <button type="button" onClick={onRetry} className="text-[12px] font-semibold text-danger hover:underline">
        Couldn’t save — retry
      </button>
    );
  }
  return null;
}

interface Change {
  label: string;
  from: string;
  to: string;
}

/** A small status pill (dot + label) for the activity feed's "changed status" line. Uses the
 *  live per-board colour rather than a fixed tone, so custom statuses render in their own hue. */
function StatusChip({ meta }: { meta: StatusMeta }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-layer px-2 py-0.5 text-[11px] font-semibold text-fg-soft">
      <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
}

/** The action phrase for an activity entry (the author is rendered separately, so
 *  this reads as its predicate: "<name> created this item"). For an edit the changed
 *  fields are listed below via itemChanges, so the phrase stays generic. */
function describeAction(a: Activity, changes: Change[], statusMetaOf: (v: string) => StatusMeta): React.ReactNode {
  switch (a.kind) {
    case 'created':
      return 'created this item';
    case 'moved': {
      const from = statusMetaOf(String(a.old));
      const to = statusMetaOf(String(a.new));
      return (
        <>
          changed status <StatusChip meta={from} /> → <StatusChip meta={to} />
        </>
      );
    }
    case 'comment':
      return 'added a comment';
    case 'attachment': {
      const fn = (a.new as { filename?: string } | null)?.filename;
      return fn ? `added attachment: ${fn}` : 'added an attachment';
    }
    case 'attachment_removed': {
      const fn = (a.old as { filename?: string } | null)?.filename;
      return fn ? `removed attachment: ${fn}` : 'removed an attachment';
    }
    case 'updated':
      return changes.length ? 'edited this item' : 'saved this item';
    default:
      return a.kind;
  }
}

/** Diffs the old/new item snapshots stored on an "updated" activity into a list of
 *  human field changes (old → new), so the feed shows WHAT changed, not just that
 *  something did. Only user-facing fields; description shows as a bare "changed". */
function itemChanges(
  oldV: unknown,
  newV: unknown,
  memberName: (id: string | null) => string | null,
  items: Item[],
  statusMetaOf: (v: string) => StatusMeta,
): Change[] {
  const o = (oldV ?? {}) as Partial<Item>;
  const n = (newV ?? {}) as Partial<Item>;
  const out: Change[] = [];
  const add = (label: string, from: string, to: string) => {
    if ((from || '') !== (to || '')) out.push({ label, from, to });
  };
  const who = (id?: string | null) => (id ? memberName(id) ?? 'someone' : '');
  const day = (d?: string | null) => (d ? fmtDate(d) : '');
  const parent = (id?: string | null) => (id ? items.find((i) => i.id === id)?.title ?? 'another item' : 'Top level');
  const epic = (id?: string | null) => (id ? items.find((i) => i.id === id)?.title ?? 'an epic' : 'None');

  add('Title', o.title ?? '', n.title ?? '');
  add('Type', o.type ? taskTypeMeta(o.type).label : '', n.type ? taskTypeMeta(n.type).label : '');
  add('Status', o.status ? statusMetaOf(o.status).label : '', n.status ? statusMetaOf(n.status).label : '');
  add('Priority', o.priority ? priorityMeta(o.priority).label : '', n.priority ? priorityMeta(n.priority).label : '');
  add('Assignee', who(o.assignee_id), who(n.assignee_id));
  add('PdM', who(o.pdm_id), who(n.pdm_id));
  add('Start date', day(o.start_date), day(n.start_date));
  add('End date', day(o.end_date), day(n.end_date));
  add('Source', (o.source ?? []).join(', '), (n.source ?? []).join(', '));
  if ((o.parent_id ?? null) !== (n.parent_id ?? null)) {
    out.push({ label: 'Parent', from: parent(o.parent_id), to: parent(n.parent_id) });
  }
  if ((o.epic_id ?? null) !== (n.epic_id ?? null)) {
    out.push({ label: 'Epic', from: epic(o.epic_id), to: epic(n.epic_id) });
  }
  if ((o.description ?? '') !== (n.description ?? '')) out.push({ label: 'Description', from: '', to: '' });
  return out;
}
